/**
 * `install` (§7.8) — independent-path install (D96: the professional/Dealer branch is P13's,
 * and `installPro`'s path choice is skipped entirely, matching the prototype's MVP view).
 * Ported from `prototype/screens/install.js`, with one necessary reordering: `POST
 * /gates/provision` is one atomic call needing `code`+`connectivity`+`defaultName`+`type`+
 * `location` together (`packages/functions/provision/schemas.ts`), so naming/location — steps
 * 8-9 in the prototype's *presentation* order — are collected before the provisioning call here,
 * not after it (implementation-notes.md → P7).
 *
 * The step order is `code → naming → created → ble → bleVerify → testOpen → owner → share → done`
 * (plus `conflict`). **P16 restored `ble`/`bleVerify`** (§7.8 step 4's credential write and its
 * live scan → connect → `PSW?` → `STA:*?` verification), which sit after `created` because both
 * need a `GateId` that does not exist until then. The standalone `provisioning` **review** stays
 * gone: the subscription/role rows it showed were static copy, not data.
 *
 * **There is no connectivity step, and `connectivity` is a constant `{kind: 'sim'}` (D172).**
 * P16 restored D55's step; **D172 removed it again, this time as a decision rather than a
 * sequencing.** With the WiFi password never accepted by the platform and nothing anywhere
 * reading the stored value back, the step asked the installer to attest to a field with no
 * consumer — and a step that collects a value no code path consumes does not ship (D138's rule,
 * applied to its own exception). **The column is therefore evidence of nothing on every gate,
 * before and after P16**, which is the honest state rather than a regression: nothing migrates,
 * and no backfill is proposed, because an invented attestation is worse than an admitted
 * constant. Do not "restore" the step as a data-quality fix without a consumer to point at.
 * **D176 adds the manager update branch, and it adds no endpoint.** When the scanned code is
 * already claimed *and the scanning installer manages that gate*, the "already installed" screen
 * offers `updateMenu` beside "start over": update name + location (`PATCH /gates/{id}`), update
 * BLE credentials (`PUT /gates/{id}/ble-credentials`, which P3's own doc already calls the
 * rotation path, Q16), or finish. **It reuses `naming`, `ble` and `bleVerify` rather than growing
 * parallel screens** — the fields, the validation and the live radio check are the same ones, and
 * a second copy of the address picker is exactly the drift D176 was written to avoid. What the
 * branch cannot do is pre-fill the BLE password: it is KMS-encrypted on write with no read-back
 * by design (D85/D94), so an update replaces both credentials rather than editing either.
 *
 * `submitProvision()` runs on `created`'s own mount, and `created` reports the one outcome that
 * matters — the gate exists, and this is its `GateId`. QR scanning is device-local and feeds the
 * same BrokerCode field as manual entry. The cloud test-open step makes a real `POST
 * /gates/{id}/open` call once the gate exists, and confirms it the same way `GateDetailScreen`
 * does (D134).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { asBrokerCode, formatGateId, type GateId, type GatePosition, type MemberRole } from '@selogate/core'
import { useSession } from '../../auth/context'
import { NetworkError } from '../../auth/transport'
import { useDeviceCache } from '../../device-cache/context'
import { AppBar, Bidi, Icon, ShareSheet, type IconName } from '../../components'
import {
  cameraSupported,
  requestCameraStream,
  setTorch,
  stopCameraStream,
  torchSupported,
  type CameraFailureReason,
} from '../../device-capabilities/camera'
import { brokerCodeFromQr, startQrScan, type QrScanSession } from '../../device-capabilities/qr-reader'
import { useLocale, useTranslation } from '../../i18n/I18nProvider'
import { useNavigation } from '../../router'
import { ContactPickerBlock } from '../shared/ContactPickerBlock'
import { LocationPicker } from '../shared/location/LocationPicker'
import { takeGateHandoff } from '../shared/gateHandoff'
import { bleAdapter } from '../../ble'
import type { BleFailureReason } from '../../ble'
import {
  closeModeOf,
  getGate,
  inviteMember,
  locationInputFromView,
  lookupBrokerClaim,
  openGate,
  patchGateDetails,
  provisionGate,
  putBleCredentials,
  type CloseMode,
  type GateDetail,
  type InviteResult,
  type LocationInput,
  type ProvisionConflict,
} from '../shared/api'
// R7/D279 — the gate-type step's two explainer cards. See the file's own header for why this is
// a screen-local stylesheet rather than four rules in `theme/global.css`.

/**
 * `created` sits between naming and the test open, and exists because provisioning is the point
 * of no return. Before it, every step is editable and carries a back button; after it, the gate
 * exists on the server with a `GateId` that nothing on this screen can change, and re-entering
 * `naming` would edit values no longer being sent. Folding that transition into `testOpen`'s own
 * mount left the screen silently doing two unrelated things — creating a gate and asking for a
 * test — and gave the user no moment where the outcome that matters (the gate exists, here is
 * its number) is the whole screen.
 */
type Step =
  | 'code'
  | 'naming'
  /**
   * **R7/D279 — between `naming` and `created`, which is the only place it can be.**
   *
   * The value rides in the provision body, so it has to be collected before the point of no
   * return; and it is a question about the *controller* rather than about the gate's identity, so
   * it does not belong inside the naming form. It is not reachable in `update` mode — the update
   * menu carries the same choice as a pill of its own, because a manager changing one field
   * should not walk a step of the install flow to do it.
   */
  | 'gateType'
  | 'created'
  | 'ble'
  | 'bleVerify'
  | 'testOpen'
  | 'conflict'
  | 'updateMenu'
  | 'owner'
  | 'share'
  | 'done'

/**
 * D176. Which job this screen is doing, and the only thing that reroutes the three shared steps.
 *
 * A **mode** rather than a set of `updateNaming`/`updateBle` steps: the steps themselves are
 * identical — same inputs, same validation, same `PUT` — and only their *exits* differ, so
 * duplicating them would be duplicating everything except the one line that varies. It starts as
 * `install` and is set exactly once, by `enterUpdate()`; nothing sets it back, because a flow that
 * entered the update branch has no gate of its own to return to provisioning.
 */
type Mode = 'install' | 'update'

/**
 * The four-pill gate-type picker is deferred along with the rest of the later-sequenced install
 * surface (D92-98) — the `type` field itself is not deferrable, since `POST /gates/provision`
 * still requires it (`packages/functions/provision/schemas.ts`). Every independent-path install
 * reports `barrier` until the picker returns; this is a UI omission, not a data model one.
 */
const DEFAULT_GATE_TYPE = 'barrier' as const

/**
 * **D172 — the constant the provision call sends, and the one thing not to mistake it for.**
 *
 * `POST /gates/provision` requires `connectivity` and `parseConnectivity` refuses `{kind:'wifi'}`
 * with no `ssid` (`422`), so `{kind:'sim'}` is the only shape that validates with nothing to
 * fill it. **It is what the schema accepts, not what the installer said** — no screen asks, and
 * `sim` on a gate record means *nobody was asked*, on every gate this product has ever
 * provisioned.
 *
 * The `wifi` branch stays in P3's schema and is unreachable from here. **The WiFi password was
 * never part of it either way** (`records.ts`'s `StoredConnectivity`): those credentials
 * configure the *controller*, over BLE, in the browser, so SeloGate never becomes the keeper of
 * a site's network password — which is also why the removed step could never have attested to
 * connectivity working, only to which kind of unit a technician believed was in the box.
 */
const PROVISION_CONNECTIVITY = { kind: 'sim' } as const

/**
 * **R7/D279 — the two close modes, in the order the pill draws them, with `auto` first.**
 *
 * 🔸 The default is *auto-close* (§2), so an installer who taps through without reading changes
 * nothing: an unwanted Close button is a control a resident can misuse, while a missing one costs
 * a remote. The three label maps are `as const satisfies` so each value stays a literal the i18n
 * key type can check, exactly as `BLE_ERROR_KEYS` above.
 */
const CLOSE_MODE_OPTIONS = ['auto', 'command'] as const satisfies readonly CloseMode[]
const CLOSE_MODE_LABELS = {
  auto: 'closeAuto',
  command: 'closeOnTap',
} as const satisfies Record<CloseMode, string>
const CLOSE_MODE_BODY = {
  auto: 'closeAutoBody',
  command: 'closeOnTapBody',
} as const satisfies Record<CloseMode, string>
/**
 * The second line of each card, and it is a second line rather than a longer first one: the head
 * says what the **gate** does and this says what the **app** shows for it, naming the control
 * (`לחצן פתיחה`) rather than the protocol. No command string appears on any screen (§2.1).
 */
const CLOSE_MODE_BODY_APP = {
  auto: 'closeAutoBodyApp',
  command: 'closeOnTapBodyApp',
} as const satisfies Record<CloseMode, string>

/** `as const satisfies` so each value stays a literal the i18n key type can check. */
const BLE_ERROR_KEYS = {
  required: 'bleRequired',
  server: 'bleFailed',
  network: 'bleNetwork',
} as const satisfies Record<'required' | 'server' | 'network', string>

const BLE_VERIFY_KEYS = {
  idle: 'checkPending',
  running: 'checkRunning',
  passed: 'checkPassed',
  failed: 'checkFailed',
  skipped: 'checkSkipped',
} as const satisfies Record<CheckState, string>

/**
 * What a failed verification actually failed at — **G-P20-e, and the reason it is worth four
 * strings rather than one.**
 *
 * The row rendered every failure as *"לא ענה"* / *"Didn't answer"*, which names one outcome and
 * describes none of the others: `unavailable` means the radio never addressed the gate at all
 * (Bluetooth off, the chooser dismissed, no Web Bluetooth in this browser), and `auth-failed`
 * means the controller answered and **refused the password** — the single most useful thing this
 * step can tell a technician, since it is precisely what step 4 exists to check. Telling them the
 * gate was silent instead sends them to look at the antenna.
 *
 * The vocabulary is `BleFailureReason`'s, unextended: this maps it to copy and adds nothing to it.
 */
const BLE_VERIFY_FAILURE_KEYS = {
  'auth-failed': 'bleVerifyRefused',
  timeout: 'checkFailed',
  disconnected: 'bleVerifyDropped',
  unavailable: 'bleVerifyNoRadio',
} as const satisfies Record<BleFailureReason, string>

/** Whether the gate has been provisioned yet — the gate on the checklist below. */
type ProvisionPhase = 'provisioning' | 'provisionFailed' | 'ready'

/**
 * One row of the test-open checklist. `skipped` is a first-class outcome, not an absence: the
 * installer may leave at any point (D138 keeps the test in the flow but never blocks on it), and
 * a row that says so is honest where a row still spinning is not.
 */
type CheckState = 'idle' | 'running' | 'passed' | 'failed' | 'skipped'

/**
 * The three checks, in the order they run: the gate answers a probe → the open command is
 * accepted → the leaf is observed moving. Splitting the old single verdict into three is what
 * makes a partial result legible — before this, a gate that answered, opened, and was still
 * travelling when the window closed reported the same "couldn't confirm" as an unpowered one.
 */
interface Checks {
  readonly status: CheckState
  readonly open: CheckState
  readonly move: CheckState
}

const CHECKS_IDLE: Checks = { status: 'idle', open: 'idle', move: 'idle' }

/**
 * D134's confirm sequence, copied from `GateDetailScreen.tsx` rather than imported — neither
 * module exports it as a shared utility, and duplicating the same shape is safer here than
 * extracting one under an unrelated PRD's file mid-brief. Keep the two in sync by hand if
 * `CONFIRM_PROBE_WINDOW_MS` ever changes server-side.
 *
 * The probes stay inside D134's 20-second confirm window and 6 seconds apart: the shortened
 * interval is 5 s with a **strict** comparison and every grant re-stamps `lastProbeAt`, so
 * tighter spacing buys rate-limited probes rather than more evidence. `CONFIRM_VERDICT_MS` is a
 * read-only tick after the last probe — the verdict must never be decided from a position read
 * in the same turn as the probe that would have produced it.
 */
const CONFIRM_POLLS_MS: readonly number[] = [6_000, 12_000, 18_000]
const CONFIRM_VERDICT_MS = 21_000

/**
 * **D140 suspends the sequence above.** Mirror of `GateDetailScreen.tsx`'s flag, and it must be
 * flipped with it — the two schedules are already hand-synchronised copies.
 *
 * Check 3 has no other evidence source: `settle()` reads `livePosition`, and only these probes
 * ever write it. So with the flag off the movement check does not *fail* — it is **not run**, and
 * says so. Reporting `failed` would be the worse lie of the two: it puts a red row and "we didn't
 * see the gate move" on an installer whose gate opened correctly, and leaves the round button
 * offering a retry that cannot ever pass. The forward path therefore falls back to check 2 — the
 * command was accepted — with copy that asks the installer to use their own eyes, which is the
 * only instrument left.
 */
const CONFIRM_POLLS_ENABLED = false

/**
 * **What counts as "the gate moved".** The old set held only the three resting positions, which
 * is what produced the defect this checklist replaces: a SEAV leaf takes seconds to travel, the
 * controller reports `opening` throughout, and every probe inside the window saw `opening` —
 * evidence of exactly the thing being verified — and reported it as a failure to confirm.
 *
 * `opening` is therefore a pass, and so is any resting position other than `closed`. `closing`
 * is deliberately absent: a gate that is closing was open and is no longer, which is a state
 * this screen must not read as a successful test open.
 */
const MOVED: ReadonlySet<GatePosition> = new Set<GatePosition>(['opening', 'open', 'stopped-while-opening'])

type QrStatus =
  'idle' | 'requesting' | 'scanning' | 'scanned' | 'invalid' | 'reader-failed' | CameraFailureReason

/**
 * One checklist row. The mark is deliberately not a `@keyframes pulse` — implementation-notes.md
 * → P7 entry 11 — and the running state gets a rotation of its own instead. `label` states what
 * is being checked and never changes; `detail` is the only part that reports.
 */
function CheckRow({
  state,
  label,
  detail,
  onRetry,
  retryLabel,
}: {
  readonly state: CheckState
  readonly label: string
  readonly detail: string
  readonly onRetry?: () => void
  readonly retryLabel?: string
}): JSX.Element {
  return (
    <li className={`install-check ${state}`}>
      <span className="install-check-mark" aria-hidden="true">
        {state === 'passed' ? <Icon name="check" size={14} /> : null}
        {state === 'failed' ? <Icon name="x" size={14} /> : null}
        {state === 'running' ? <span className="install-check-spin" /> : null}
      </span>
      <span className="install-check-text">
        <span className="install-check-label">{label}</span>
        <span className="install-check-detail">{detail}</span>
      </span>
      {onRetry !== undefined && retryLabel !== undefined ? (
        <button className="install-check-retry" type="button" onClick={onRetry}>
          {retryLabel}
        </button>
      ) : null}
    </li>
  )
}

/**
 * The test open itself — a **green tile above the row that reports it**, not the row.
 *
 * The two were briefly one control, which put the action and its verdict in the same box: the
 * tile had to say *"tap me"* and *"here is what happened"* out of one line of copy, and the
 * green that means *this is the deliberate action on the screen* could not be worn by something
 * that also has to render a failure. Separated, each says one thing — and the order is what ties
 * them back together, the button sitting directly above the row it writes to.
 *
 * It is a **list item**, because it belongs to the checklist's sequence rather than sitting
 * beside it; `<li><button></button></li>` is ordinary markup, and the alternative — two lists of
 * one item with a control between them — announces "list of 1" twice for a shape that is one
 * list.
 */
function TestOpenTile({
  label,
  onOpen,
  inactive,
  busy,
}: {
  readonly label: string
  readonly onOpen: () => void
  /** The precondition has not been met — greyed, and not tappable. */
  readonly inactive: boolean
  /** A command is outstanding. Not tappable, and deliberately not greyed: it is working. */
  readonly busy: boolean
}): JSX.Element {
  return (
    <li className={`install-open-row${inactive ? ' inactive' : ''}`}>
      <button
        className="install-open-tile"
        type="button"
        disabled={inactive || busy}
        aria-busy={busy}
        onClick={onOpen}
      >
        <Icon name="door-open" size={22} />
        <span>{label}</span>
      </button>
    </li>
  )
}

/**
 * D267 — `updateFor` is the second door onto D176's update branch, and it changes *where this
 * screen starts and where it goes back to*, never what either editor does.
 *
 * Present, the screen opens on `updateMenu` in `mode: 'update'` for that gate; absent, it is the
 * install flow exactly as before. The two entries differ only in what sits behind the menu —
 * an unreturnable scan on D176's, nothing at all on this one — which is what `updateOrigin`
 * exists to tell apart.
 */
export function InstallScreen({ updateFor }: { readonly updateFor?: GateId }): JSX.Element {
  const session = useSession()
  const deviceCache = useDeviceCache()
  const navigate = useNavigation()
  const { t } = useTranslation('install')
  const { locale } = useLocale()

  /**
   * **What the Access page already read, taken once and during render** — `AccessScreen`'s own
   * `takenRef` shape, and here for its two reasons. Taken during render because the whole value
   * of the handoff is that it is available for the *first* paint: seeded from an effect, the menu
   * paints one frame with an empty name line first, which is D265's own defect. Cached in a ref
   * because the slot is read-once and `StrictMode` invokes the component body twice — a second
   * take answers `undefined` and would blank what the first one filled.
   *
   * A miss is not an error (`gateHandoff.ts` rule 4): a cold deep link straight to this URL falls
   * through to `loadGateForUpdate`, which is D176's own path unchanged.
   */
  const handedRef = useRef<{ readonly gateId: GateId; readonly gate?: GateDetail } | undefined>(undefined)
  if (
    updateFor !== undefined &&
    (handedRef.current === undefined || handedRef.current.gateId !== updateFor)
  ) {
    const gate = takeGateHandoff(updateFor, 'gate')
    handedRef.current = { gateId: updateFor, ...(gate === undefined ? {} : { gate }) }
  }
  const handedGate = handedRef.current?.gate

  const [step, setStep] = useState<Step>(updateFor === undefined ? 'code' : 'updateMenu')

  // Step: code
  //
  // D112 makes QR and manual equal acquisition paths, so the default is a UX choice, not a
  // hierarchy: lead with the camera where there is one, and land straight on the working path
  // where there is not — an installed iOS PWA without `getUserMedia` should never be shown a
  // scan button whose only outcome is a failure message. Both chips stay, and the QR tab is
  // honest about why it is empty. Read once: support cannot change mid-screen.
  const cameraAvailable = useMemo(() => cameraSupported(), [])
  const [codeMethod, setCodeMethod] = useState<'qr' | 'manual'>(cameraAvailable ? 'qr' : 'manual')
  const [code, setCode] = useState('')
  /**
   * **Two different refusals, so two different strings.** `'format'` is `asBrokerCode()` throwing
   * locally — the code is not six characters — and keeps `codeInvalid`'s existing copy. `'claim'`
   * is the server's `422 field:'code'` on `POST /gates/provision`: the code is well-formed but the
   * server would not accept it, which `codeInvalid`'s "enter a 6-character code" does not describe
   * and `codeHelp` cannot either. `codeUnknownOrFailed` was written for exactly this branch
   * (G9.3) and wired nowhere until now — R1-7(c).
   */
  const [codeError, setCodeError] = useState<'format' | 'claim' | undefined>(undefined)
  /**
   * D151. `continueFromCode()`'s courtesy read (`GET /gates/claim/{brokerCode}`) is in flight —
   * disables Continue and swaps its label, the same shape `ownerBusy`/`bleSaving` already
   * use elsewhere on this screen. Never blocks the flow on its own: see `continueFromCode`.
   */
  const [codeCheckBusy, setCodeCheckBusy] = useState(false)
  const [qrStatus, setQrStatus] = useState<QrStatus>('idle')
  const videoRef = useRef<HTMLVideoElement>(null)
  const scanSessionRef = useRef<QrScanSession | undefined>(undefined)
  const scanAbortRef = useRef<AbortController | undefined>(undefined)
  const scanAttemptRef = useRef(0)
  const scanActiveRef = useRef(false)
  /**
   * A1 — guards the mount-fired auto-start against `main.tsx`'s `StrictMode` double-invoked
   * effects, the same shape as `provisionStartedRef` below. A **ref**, not state:
   * `startScanning()` calls `stopScanner()` first, which bumps `scanAttemptRef`, so an unguarded
   * double invoke would call `startScanning()` a second time and that second call's own
   * `stopScanner()` would abort the first call's own in-flight request. `selectCodeMethod('qr')`
   * clears it, so re-selecting the QR chip after being on manual re-arms the same sequence.
   */
  const autoStartedRef = useRef(false)
  /**
   * The torch pill. `torchAvailable` is a *capability* answer taken once from the live track, so
   * the control is absent on a device that has no lamp rather than present and inert — the same
   * posture `cameraSupported()` gives the Scan chip one level up.
   *
   * The stream is held here because nothing else on this screen has it: `startQrScan()` takes
   * ownership of the `MediaStream` for cleanup, and neither it nor `QrScanSession` hands the
   * track back out. The ref is cleared by `stopScanner()` alongside the session, so a stopped
   * scan can never leave a stale track behind for the pill to address.
   */
  const scanStreamRef = useRef<MediaStream | undefined>(undefined)
  const [torchAvailable, setTorchAvailable] = useState(false)
  const [torchOn, setTorchOn] = useState(false)

  // Steps: ble / bleVerify (§7.8 step 4, restored by P16)
  //
  // **The password is in component state and reaches no store, no log, and no analytics** — the
  // same rule `web/src/ble/credentials.ts` keeps for a *fetched* credential, applied to one being
  // *set*. It leaves this screen only in the `PUT /gates/{id}/ble-credentials` body, where P3
  // KMS-encrypts it before it is stored (D84/D85).
  const [bleName, setBleName] = useState('')
  const [blePassword, setBlePassword] = useState('')
  const [showBlePassword, setShowBlePassword] = useState(false)
  const [bleError, setBleError] = useState<'required' | 'server' | 'network' | undefined>(undefined)
  const [bleBusy, setBleBusy] = useState(false)
  /** The live verification's own three-state row — the same vocabulary the test-open checks use. */
  const [bleVerifyState, setBleVerifyState] = useState<CheckState>('idle')
  // Why the last verification failed, so the row can say which failure it was. Cleared on every
  // fresh run; never carries a message, only the closed reason the adapter returned.
  const [bleVerifyReason, setBleVerifyReason] = useState<BleFailureReason | undefined>(undefined)

  // Step: naming
  const [defaultName, setDefaultName] = useState(handedGate?.defaultName ?? '')
  /**
   * P17. Was a bare address string; it is now the whole D51 payload, because "use current
   * location" and Places contribute `lat`/`lng`/`placeId`/`source` that the string cannot carry.
   * The initial value is what P7 shipped and what every degraded path returns to — free text with
   * `source: manual` and no coordinates. D267 seeds it from the handoff where there is one, which
   * is the same value `loadGateForUpdate` would have arrived at one round trip later.
   */
  const [location, setLocation] = useState<LocationInput>(
    handedGate === undefined
      ? { formatted: '', source: 'manual' }
      : locationInputFromView(handedGate.location),
  )
  const [namingError, setNamingError] = useState<'name' | 'location' | undefined>(undefined)

  /**
   * Step: gateType — **and the update menu's pill, which is deliberately the same state.**
   *
   * One value, two surfaces: the install step collects it for the provision body, and the update
   * menu edits it on a gate that already exists. `defaultName` and `location` above are shared
   * between the two jobs for exactly this reason, and a second `updateCloseMode` beside this one
   * would be two things that mean the same and drift.
   *
   * **The seed is `closeModeOf`, never a bare `'auto'`** — that helper is the single place the
   * absent-reads-as-auto default resolves (D279), and a `?? 'auto'` here would be the second copy
   * of a default that eventually disagrees with itself. On the install path there is no gate, so
   * it answers `'auto'`, which is §2's chosen default for a new install.
   */
  const [closeMode, setCloseMode] = useState<CloseMode>(closeModeOf(handedGate))

  // Provisioning result
  const [gateId, setGateId] = useState<GateId | undefined>(undefined)
  const [provisionError, setProvisionError] = useState(false)
  const [provisionConflict, setProvisionConflict] = useState<ProvisionConflict | undefined>(undefined)
  // D147. A `409` whose body `readProvisionConflict` could not parse — still a duplicate-claim
  // refusal, just one with no gate to name. The `conflict` step renders for this too, with
  // `duplicateBodyUnknown` copy and no "open existing" (there is no `viewer` to read one from).
  const [conflictUnparseable, setConflictUnparseable] = useState(false)
  /**
   * D151. `conflict` is now reachable from two places — the scan step's own courtesy read, and
   * `submitProvision`'s authoritative `409` — and back must return wherever the installer actually
   * came from rather than always assuming `naming`. Defaults to `naming` because that path never
   * sets it explicitly wrong: `submitProvision` only ever runs after `naming`'s own continue.
   */
  const [conflictOrigin, setConflictOrigin] = useState<'code' | 'naming'>('naming')

  /*
   * D176 — the manager update branch.
   *
   * `updateGateId` is deliberately **not** `gateId`. That one means "this run provisioned a gate,"
   * and three things read it as exactly that: `created`'s back affordance disappears once it is
   * set (D138), `submitProvision`'s mount guard checks it, and `finish()` assumes a gate this flow
   * created. Borrowing it for a gate that already existed would make all three quietly wrong.
   * `targetGateId` below is the one place the two are unified, for the two calls that only need
   * "which gate am I writing to."
   */
  const [mode, setMode] = useState<Mode>(updateFor === undefined ? 'install' : 'update')
  const [updateGateId, setUpdateGateId] = useState<GateId | undefined>(updateFor)
  /**
   * D267 — which door the manager came through, and the only thing the two entries disagree
   * about. `enterUpdate` leaves it `'conflict'`; the `installEdit` route seeds `'route'`.
   *
   * Two exits read it. D176's back and *Done* both assume the scan is behind this menu: back
   * returns to the duplicate screen, and *Done* `resetTo`s Gate Detail **because** every install
   * step behind it is unreturnable — `code` would rescan an installed gate, and D138's
   * provision-on-mount guard makes a return to `naming` a `409`. Neither is true on the route
   * entry, where the manager came from the Access page and nothing unreturnable is behind them,
   * so both become `navigate.back()`.
   */
  const [updateOrigin] = useState<'conflict' | 'route'>(updateFor === undefined ? 'conflict' : 'route')
  /**
   * D267 — the scanner's role on the gate being edited, where it is known.
   *
   * `undefined` means *not established yet*, never *not a manager*: only a **confirmed**
   * `'resident'` refuses, which is D153's rule for an absent field applied to this one. On
   * D176's entry the conflict body already carries it (and gates the offer that reaches this
   * branch at all); on D267's it comes off the handoff or the record read.
   */
  const [updateRole, setUpdateRole] = useState<'manager' | 'resident' | undefined>(handedGate?.viewer.role)
  /**
   * Whether the existing gate's own record has been read yet. Only the **address** needs it — the
   * conflict body already carries `defaultName` — and it gates the name/location button rather
   * than failing open, because entering that screen with an empty required address field asks a
   * manager to retype an address the server already holds, and a retype is where a good address
   * silently becomes a worse one. BLE needs no read and is never gated on this.
   */
  const [updateLoad, setUpdateLoad] = useState<'loading' | 'ready' | 'failed'>(
    handedGate === undefined ? 'loading' : 'ready',
  )
  const [updateBusy, setUpdateBusy] = useState(false)
  const [updateError, setUpdateError] = useState<'server' | 'network' | undefined>(undefined)
  /** Set by a successful save so the menu can say so; cleared on the way into either editor. */
  const [updateSaved, setUpdateSaved] = useState(false)
  /**
   * The gate the two credential calls write to, whichever job this screen is doing. Read only by
   * `submitBleCredentials` and `runBleVerify`, which genuinely do not care how the gate got here.
   * Everything else keeps reading whichever of the two it actually means.
   */
  const targetGateId = mode === 'update' ? updateGateId : gateId

  // Step: testOpen
  const [provisionPhase, setProvisionPhase] = useState<ProvisionPhase>('provisioning')
  const [checks, setChecks] = useState<Checks>(CHECKS_IDLE)
  // The status check reads its verdict from the device cache's own LED phase, which is only
  // trustworthy once the probe has actually been seen in flight — `statusPhase()` reports
  // `stale` both before a probe starts and after one returns no evidence, and only this flag
  // tells the two apart. Reset with the check itself so a retry re-arms it.
  const probeObservedRef = useRef(false)
  // The second half of the same guard, and the one that keeps the row from spinning forever:
  // `probeGate()` returns early without ever marking the gate `probing` when the session is not
  // signed in, so `checking` is never observed and the flag above never flips. Bumping this on
  // the probe's own settlement re-renders, which re-runs the resolving effect and lets it read
  // the (unchanged, therefore failing) phase instead of waiting for a state that never comes.
  const statusRunRef = useRef(0)
  const [statusProbeSettled, setStatusProbeSettled] = useState(0)
  // Guards the mount-fired `submitProvision()` against React 18 StrictMode's double-invoked
  // effects in dev, and is deliberately reset (not just left true) whenever the back button
  // sends the user to `naming` to fix something and try again.
  const bleVerifyStartedRef = useRef(false)
  const provisionStartedRef = useRef(false)
  /**
   * D267 — the record read for a route entry that arrived with no handoff, fired once.
   *
   * A **ref** for `provisionStartedRef`'s reason: `main.tsx` renders under `StrictMode`, whose
   * double-invoked mount effect would otherwise issue two `GET /gates/{gateId}` for one screen.
   * Never cleared — this entry reads once and `updateLoadFailed`'s retry is the way to ask again.
   */
  const updateReadStartedRef = useRef(false)
  const confirmTimers = useRef<number[]>([])
  // Read through a ref so a timer scheduled several renders ago still calls the current cache
  // (same reasoning as `GateDetailScreen`'s `deviceCacheRef`).
  const deviceCacheRef = useRef(deviceCache)
  deviceCacheRef.current = deviceCache

  // Owner. **`manager` is the default here and `resident` is in `invite`** — D43's handoff is
  // "add owner as co-manager," so the install flow's whole purpose for this step is the manager
  // grant; a resident owner is the deviation worth a deliberate tap.
  const [ownerPhone, setOwnerPhone] = useState('')
  const [ownerRole, setOwnerRole] = useState<MemberRole>('manager')
  /**
   * R1-7(e). `sendOwnerInvite` used to render `ownerPhoneRequired` — "enter the owner's phone
   * number" — for every failure (403/409/422/500), telling the installer they had not typed a
   * phone number they were looking at. Typed like the Preferences rename's `SaveState`: `'invalid'`
   * is a `422` on the phone field, `'generic'` is everything else this screen cannot name more
   * specifically (including a `NetworkError`).
   */
  const [ownerError, setOwnerError] = useState<'invalid' | 'generic' | undefined>(undefined)
  // No in-flight flag existed at all here — worse than a stuck one, since a double-tap sent two
  // invites. `ownerBusy` both disables the button and clears in `finally`.
  const [ownerBusy, setOwnerBusy] = useState(false)
  const [invite, setInvite] = useState<InviteResult | undefined>(undefined)

  const stopScanner = useCallback((): void => {
    scanAttemptRef.current += 1
    scanActiveRef.current = false
    // The abort covers the window before `startQrScan` resolves, where there is no session yet;
    // the session covers everything after. Neither alone stops the camera in every state.
    scanAbortRef.current?.abort()
    scanAbortRef.current = undefined
    scanSessionRef.current?.stop()
    scanSessionRef.current = undefined
    // The track is stopped by whichever of the two above owns it; what is cleared here is this
    // screen's handle on it, so the pill cannot address a dead track. The lamp goes out with the
    // track itself — there is no `setTorch(stream, false)` to await, and awaiting one inside a
    // synchronous stop would be a promise nobody holds.
    scanStreamRef.current = undefined
    setTorchAvailable(false)
    setTorchOn(false)
  }, [])

  useEffect(() => {
    const stopWhenHidden = (): void => {
      if (document.hidden && scanActiveRef.current) {
        stopScanner()
        setQrStatus('idle')
      }
    }
    document.addEventListener('visibilitychange', stopWhenHidden)
    return () => {
      document.removeEventListener('visibilitychange', stopWhenHidden)
      stopScanner()
    }
  }, [stopScanner])

  /**
   * A1 — the camera opens on its own the moment this screen lands on the QR method. The
   * installer is standing at the gate and the camera *is* the point of this screen, so this
   * deliberately fires `getUserMedia` with no user activation — a product decision, not an
   * oversight.
   *
   * **What that costs, and how it is paid.** `qrRationale` is D73 §B2a's just-in-time camera
   * purpose string, whose posture is that it renders *before* the prompt; it used to earn that
   * by sitting behind the `qrStart` tap this effect replaces. It is now rendered
   * unconditionally on the QR tab instead, which still precedes the prompt because effects run
   * after the commit that paints it. Anything that moves this call earlier than the first paint
   * breaks that, and the notice with it.
   *
   * **Gated on `step === 'code'`, and that guard is the whole of what keeps D267's second door
   * out of the camera.** `codeMethod` is seeded `'qr'` from `cameraSupported()`, which reads the
   * API's presence and prompts for nothing — so on a screen opened at `updateMenu` this effect
   * still matched, and `שינויי התקנה` asked for the camera before painting a menu that has no
   * viewfinder on it. The viewfinder is rendered by the `code` branch and by nothing else, so the
   * request resolved into a `videoRef` of `null` and ended at `reader-failed`: a permission prompt
   * spent on an outcome no step could use. **The rendered surface is what licenses the request** —
   * anything that starts the camera from a step that does not draw a viewfinder is this defect
   * again, whatever the reason.
   *
   * Keyed on `codeMethod` and `step`, **not** `qrStatus`. The `visibilitychange` handler above also
   * drives `qrStatus` back to `'idle'` when the tab hides; keying this effect on `qrStatus` too
   * would re-run it on that transition and restart the camera the instant the tab is hidden.
   * Because neither `codeMethod` nor `step` has changed, that transition is invisible to this
   * effect and the guard never resets. **`step` is in the list because the effect reads it, and
   * that costs nothing today**: no path reaches `code` with `autoStartedRef` still unset — the
   * install door mounts there, and the update door's only exits are its two editors and the
   * router's own back, `startOver` living on `conflict`, which a route-origin update never
   * reaches. What the dependency buys is that a future step landing on `code` first gets the
   * viewfinder it is owed rather than inheriting this accident.
   *
   * Never re-fires from a failure or a denial, for the same reason: `denied`/`unavailable`/
   * `failed`/etc. leave both keys unchanged, so `autoStartedRef` stays set and the screen's
   * existing explicit retry button (`qrRetry`) is the only way back in. Re-prompting a user who
   * just refused the camera on every mount is exactly the failure mode this guards against.
   */
  useEffect(() => {
    if (step !== 'code' || codeMethod !== 'qr' || autoStartedRef.current || qrStatus !== 'idle') return
    autoStartedRef.current = true
    void startScanning()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeMethod, step])

  async function startScanning(): Promise<void> {
    stopScanner()
    const attempt = scanAttemptRef.current
    const abort = new AbortController()
    scanAbortRef.current = abort
    scanActiveRef.current = true
    setCode('')
    setCodeError(undefined)
    setQrStatus('requesting')

    const camera = await requestCameraStream()
    if (attempt !== scanAttemptRef.current) {
      if (camera.ok) stopCameraStream(camera.stream)
      return
    }
    if (!camera.ok) {
      scanActiveRef.current = false
      setQrStatus(camera.reason)
      return
    }

    const video = videoRef.current
    if (video === null) {
      scanActiveRef.current = false
      stopCameraStream(camera.stream)
      setQrStatus('reader-failed')
      return
    }

    setQrStatus('scanning')
    scanStreamRef.current = camera.stream
    setTorchAvailable(torchSupported(camera.stream))
    setTorchOn(false)
    try {
      const session = await startQrScan(
        camera.stream,
        video,
        (raw) => {
          if (attempt !== scanAttemptRef.current) return
          scanActiveRef.current = false
          const scanned = brokerCodeFromQr(raw)
          if (scanned === undefined) {
            setCode('')
            setCodeError('format')
            setQrStatus('invalid')
            return
          }
          setCode(scanned)
          setCodeError(undefined)
          setQrStatus('scanned')
        },
        { signal: abort.signal },
      )
      if (attempt !== scanAttemptRef.current) session.stop()
      else scanSessionRef.current = session
    } catch {
      if (attempt === scanAttemptRef.current) {
        scanActiveRef.current = false
        setQrStatus('reader-failed')
      }
    }
  }

  /**
   * Toggles the lamp and renders whatever the track ended up in. `setTorch()` returns the real
   * state rather than the requested one, so a constraint the driver refuses leaves the pill off
   * instead of lit over a dark viewfinder.
   */
  async function toggleTorch(): Promise<void> {
    const stream = scanStreamRef.current
    if (stream === undefined) return
    setTorchOn(await setTorch(stream, !torchOn))
  }

  function selectCodeMethod(method: 'qr' | 'manual'): void {
    if (method === codeMethod) return
    stopScanner()
    setQrStatus('idle')
    setCodeError(undefined)
    setCodeMethod(method)
    // A1 — re-arms the auto-start effect. Tapping the QR chip after being on manual is a manual
    // accelerant for the same sequence the mount effect runs, not a one-time thing it did once.
    if (method === 'qr') autoStartedRef.current = false
  }

  /**
   * D151. Before the format check even had a `409` to catch, a duplicate code sat undetected
   * until `naming` had been filled in and `created` mounted. `GET /gates/claim/{brokerCode}`
   * answers from the code alone, so a claimed code now routes straight to `conflict` from here.
   *
   * **The `409` stays the authority.** This is a courtesy read in front of it, never a
   * replacement — `contracts.md` §1c, restated by D151. Anything short of a clean `claimed: true`
   * answer (an unclaimed code, a non-2xx response, an unparseable body, or a thrown
   * `NetworkError`) proceeds to `naming` exactly as it did before this existed: the pre-check must
   * never strand the installer on its own failure, because `submitProvision`'s own `409` still
   * catches a genuine duplicate at submit time.
   */
  async function continueFromCode(): Promise<void> {
    let normalized: string
    try {
      normalized = asBrokerCode(code)
    } catch {
      setCodeError('format')
      return
    }
    setCode(normalized)
    setCodeError(undefined)
    setProvisionConflict(undefined)
    setConflictUnparseable(false)
    setCodeCheckBusy(true)
    try {
      const result = await lookupBrokerClaim(session.request, normalized)
      if (result.ok && result.data.claimed === true) {
        setConflictOrigin('code')
        setProvisionConflict(result.data)
        setStep('conflict')
        return
      }
      setStep('naming')
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      // D151's courtesy read failing is not a reason to stop: the `409` at submit stays the
      // authoritative answer, and this read never blocks the flow on its own.
      setStep('naming')
    } finally {
      setCodeCheckBusy(false)
    }
  }

  /**
   * §7.8 step 4 — write the credentials, then go and prove they work.
   *
   * **This screen sets; it never reads.** `PUT /gates/{id}/ble-credentials` is P3's, it stores a
   * KMS-encrypted password, and there is no path from here to the plaintext again — a resident's
   * JIT `GET /gates/{id}/ble-access` is P16's own endpoint and a different flow entirely
   * (P16 "Explicitly out of scope": *"a technician verifying a gate and a resident opening one
   * share a radio, not a flow"*).
   *
   * **The step is skippable and the flow does not block on it.** A gate with no BLE credentials
   * is a perfectly good gate — residents open over cloud, which D4 made the primary path — and a
   * technician who cannot get the radio working at 7pm should be able to finish the install
   * rather than abandon it. That is the same posture the test-open step already takes.
   */
  async function submitBleCredentials(): Promise<void> {
    if (bleName.trim().length === 0 || blePassword.length === 0) {
      setBleError('required')
      return
    }
    setBleError(undefined)
    setBleBusy(true)
    try {
      const result = await putBleCredentials(session.request, targetGateId!, {
        bleName: bleName.trim(),
        blePassword,
      })
      setBleBusy(false)
      if (!result.ok) {
        setBleError('server')
        return
      }
      setBleVerifyState('idle')
      setStep('bleVerify')
    } catch (error) {
      setBleBusy(false)
      setBleError('network')
      if (!(error instanceof NetworkError)) throw error
    }
  }

  /**
   * §7.8 step 4's **live** half — scan → connect → `PSW?<password>` → `STA:*?`, against the real
   * controller, from the technician's own browser.
   *
   * **It uses the password the installer just typed, not one fetched back from the server**, and
   * that is the point of running it here: it proves the value that was *stored* is the value the
   * controller *accepts*, which no server-side check can establish. The credential never leaves
   * this component to do it.
   *
   * `STA:*?` rather than an open: the verification must not actuate a gate somebody may be
   * standing at. The test open is its own step, right after, and it is the one that moves a leaf.
   */
  async function runBleVerify(): Promise<void> {
    setBleVerifyState('running')
    setBleVerifyReason(undefined)
    try {
      const result = await bleAdapter().verify({
        gateId: targetGateId!,
        bleName: bleName.trim(),
        blePassword,
        // The adapter's shape wants an expiry; nothing consults it on this path, because the
        // credential is the one just typed rather than one fetched and held (`ble/credentials.ts`
        // is the module with a lifetime, and this screen never enters it).
        expiresAt: new Date(Date.now() + 60_000).toISOString(),
      })
      /*
       * **`'refused'` is narrowed out rather than mapped** (R4/D216). This step sends
       * `STATUS_QUERY`, which is answered with a `STA:` burst and never with a `CMD:` frame, so
       * the controller has nothing here to refuse and `readBurst` cannot reach that verdict. The
       * map above stays `BleFailureReason`'s, unextended — a row claiming to explain an outcome
       * it cannot see is worse than the generic one. If it ever did arrive, the row reports a
       * plain failure, which is true.
       */
      if (!result.ok && result.reason !== 'refused') setBleVerifyReason(result.reason)
      setBleVerifyState(result.ok ? 'passed' : 'failed')
    } catch {
      // A thrown adapter is a failed verification and nothing more. There is no classification to
      // report here that the row's own three states do not already carry, and an error object on
      // this path is one field away from the password that produced it.
      setBleVerifyState('failed')
    }
  }

  function continueFromNaming(): void {
    if (defaultName.trim().length === 0) {
      setNamingError('name')
      return
    }
    // The address stays required on every path (D51/D55) — the two new inputs give it more ways to
    // be filled, never a way to be skipped. `formatted` is the one field the map (D33), the
    // dashboard, and שעון שבת's locality lookup (D41) all depend on.
    if (location.formatted.trim().length === 0) {
      setNamingError('location')
      return
    }
    setNamingError(undefined)
    // D176. The same two fields, the same two validations, and then either the point of no return
    // or a `PATCH` on a gate that already exists. Validation is deliberately shared: an update
    // that let the address go blank would be a way to erase one, and `PATCH /gates/{id}` has no
    // opinion about a `formatted` of `''`.
    if (mode === 'update') {
      void submitGateEdit()
      return
    }
    // R7/D279 — the gate-type step sits between here and the point of no return; `created`'s own
    // mount is still what provisions, and its guard is untouched.
    setStep('gateType')
  }

  /**
   * D176 — `PATCH /gates/{gateId}` with both fields, then back to the menu.
   *
   * `deviceCache.refresh()` because `defaultName` is what Home's tile renders: leaving the cache
   * alone would show the manager a rename that visibly did not happen. Fired and not awaited, the
   * same treatment `finish()` gives it.
   */
  async function submitGateEdit(): Promise<void> {
    setUpdateError(undefined)
    setUpdateBusy(true)
    try {
      const result = await patchGateDetails(session.request, updateGateId!, {
        defaultName: defaultName.trim(),
        location,
      })
      if (!result.ok) {
        setUpdateError('server')
        return
      }
      void deviceCache.refresh()
      setUpdateSaved(true)
      setStep('updateMenu')
    } catch (error) {
      setUpdateError('network')
      if (!(error instanceof NetworkError)) throw error
    } finally {
      setUpdateBusy(false)
    }
  }

  /**
   * **R7/D279 — the update menu's close-mode pill, saved on the tap (§7 D-f).**
   *
   * D247 put the *My account* settings behind one Save in the app bar; this control deliberately
   * does not take that shape, and the difference is that D247's screen has three fields and a
   * draft. This is one field with two values, and a sub-screen with a Save button would be three
   * taps to change a radio button.
   *
   * **The thumb moves on the ECHOED value and never on the tap.** `PATCH /gates/{gateId}` returns
   * the gate projection, so the value that comes back is what the store now holds — the same rule
   * D133/D247 apply to the profile rename, and the reason a refused or partially-applied write
   * cannot leave a manager looking at a mode the gate does not have.
   *
   * `deviceCache.refresh()` for `submitGateEdit`'s own reason: `closeMode` is in the snapshot's
   * allow-list, and Home paints its tile from that snapshot without ever issuing a gate read
   * (D141), so leaving the cache alone would show a change that visibly did not happen.
   */
  async function saveCloseMode(next: CloseMode): Promise<void> {
    if (updateGateId === undefined) return
    setUpdateError(undefined)
    setUpdateSaved(false)
    setUpdateBusy(true)
    try {
      const result = await patchGateDetails(session.request, updateGateId, { closeMode: next })
      if (!result.ok) {
        setUpdateError('server')
        return
      }
      setCloseMode(closeModeOf(result.data))
      void deviceCache.refresh()
      setUpdateSaved(true)
    } catch (error) {
      setUpdateError('network')
      if (!(error instanceof NetworkError)) throw error
    } finally {
      setUpdateBusy(false)
    }
  }

  /**
   * D176's entry point, from the "already installed" screen and nowhere else.
   *
   * The name is pre-filled from the conflict body, which already carries it; the address needs
   * `GET /gates/{gateId}`, whose manager projection is the only thing that returns one (P3 sends
   * `lat`/`lng`/`placeId` to managers only). The read is fired here rather than from the menu's
   * own mount so that a retry re-enters the same function.
   */
  function enterUpdate(conflict: ProvisionConflict): void {
    setMode('update')
    const target = conflict.gateId as GateId
    setUpdateGateId(target)
    setUpdateRole(conflict.viewer?.role)
    setDefaultName(conflict.defaultName)
    setNamingError(undefined)
    setUpdateError(undefined)
    setUpdateSaved(false)
    setStep('updateMenu')
    void loadGateForUpdate(target)
  }

  async function loadGateForUpdate(target: GateId): Promise<void> {
    setUpdateLoad('loading')
    try {
      const result = await getGate(session.request, target)
      if (!result.ok) {
        setUpdateLoad('failed')
        return
      }
      // The projection is the newer authority on the name too — a co-manager may have renamed the
      // gate since whatever the claim row was written with.
      setDefaultName(result.data.defaultName)
      setLocation(locationInputFromView(result.data.location))
      // The projection is the newer authority on the viewer's role too — the handoff may have
      // carried a cached one, and a membership can have changed since it was written.
      setUpdateRole(result.data.viewer.role)
      // R7/D279 — the menu's pill is pre-filled from this record and from nothing else, which is
      // why it renders only once `updateLoad` is `'ready'`: a pill drawn on a guess would show a
      // manager the wrong mode as settled and take a tap to "change" it to what it already was.
      setCloseMode(closeModeOf(result.data))
      setUpdateLoad('ready')
    } catch (error) {
      setUpdateLoad('failed')
      if (!(error instanceof NetworkError)) throw error
    }
  }

  /**
   * D176's exit. `resetTo` rather than `navigate`, and Gate Detail rather than Home: the install
   * flow's own entries are behind this one and none of them can be returned to — `code` would
   * re-scan a gate that is already installed — and the gate just edited is the surface that shows
   * what changed.
   */
  function finishUpdate(): void {
    void deviceCache.refresh()
    // D267 — `resetTo` is D176's answer to *what is behind this menu*, not a preference for Gate
    // Detail. On the scan entry the answer is the install flow, none of whose steps can be
    // returned to; on the `installEdit` entry it is the Access page the manager came from, which
    // can. So the exit follows the door rather than the destination.
    if (updateOrigin === 'route') {
      navigate.back()
      return
    }
    navigate.resetTo({ id: 'gate', gateId: updateGateId! })
  }

  function clearConfirmTimers(): void {
    for (const timer of confirmTimers.current) window.clearTimeout(timer)
    confirmTimers.current = []
  }

  const setCheck = useCallback((id: keyof Checks, state: CheckState): void => {
    setChecks((prev) => ({ ...prev, [id]: state }))
  }, [])

  async function submitProvision(): Promise<void> {
    setProvisionPhase('provisioning')
    setChecks(CHECKS_IDLE)
    setProvisionError(false)
    setProvisionConflict(undefined)
    setConflictUnparseable(false)
    // D151. This path only ever runs after `naming`'s own continue (`created`'s mount effect), so
    // a `409` reached from here always backs to `naming` — never `code`, which `continueFromCode`
    // sets for itself.
    setConflictOrigin('naming')
    // R1-6. This `await` had nothing catching it: a `NetworkError` left `provisionPhase` at
    // `'provisioning'` ("ממתין…") forever, with no error and no retry — `FIX NOW` per the
    // priority list, since it is the point of no return the whole flow hinges on.
    try {
      const result = await provisionGate(session.request, {
        code,
        mode: 'independent',
        defaultName,
        type: DEFAULT_GATE_TYPE,
        location,
        connectivity: PROVISION_CONNECTIVITY,
        // R7/D279 — what the `gateType` step collected. `ProvisionBody` **names** the field, and
        // that is load-bearing rather than tidy: a spread into an object literal bypasses
        // TypeScript's excess-property check (D232), so a body shape that did not name it would
        // compile clean and drop it, recording every install as auto-close.
        closeMode,
      })
      if (!result.ok) {
        if (result.status === 422) {
          if (result.field === 'code') {
            provisionStartedRef.current = false
            setCodeError('claim')
            setStep('code')
            return
          }
          if (
            result.field === 'defaultName' ||
            result.field === 'type' ||
            result.field?.startsWith('location.')
          ) {
            provisionStartedRef.current = false
            setNamingError(result.field?.startsWith('location.') ? 'location' : 'name')
            setStep('naming')
            return
          }
          // A `connectivity` `422` has **no field-specific branch on purpose (D172)**: the value
          // is a constant this screen sends, so the installer typed nothing that could be wrong
          // and there is no step to send them back to. It falls through to the generic
          // provision-failure state, which is the honest answer — a re-route to a screen that
          // does not exist is worse than a retry button.
          // Any other `422` falls through to the generic provision-failure state below.
        }
        if (result.status === 409) {
          // D147. `provisionGate()` already tried to parse this as a duplicate-claim conflict
          // (`readProvisionConflict`) and could not, so `result` carries no gate to name and no
          // `viewer` to offer "open existing" from — but it is still a duplicate-claim shape, and
          // the generic provision-failure retry below can only ever `409` again on the same code.
          // Route to the same escape a parsed conflict gets, with nothing to show but that.
          provisionStartedRef.current = false
          setConflictUnparseable(true)
          setStep('conflict')
          return
        }
        setProvisionError(true)
        setProvisionPhase('provisionFailed')
        return
      }
      if ('code' in result.data) {
        provisionStartedRef.current = false
        setProvisionConflict(result.data)
        setStep('conflict')
        return
      }
      setGateId(result.data.gateId as GateId)
      setProvisionPhase('ready')
      /**
       * **Here, not in `finish()`, is what makes the new gate already be on Home when the user
       * arrives.** Nothing else re-reads `GET /gates` after a provision — the cache refreshes on
       * boot and on a reconnect, and neither happens during an install — so the gate the installer
       * just created was missing from Home until the app was next relaunched or reception flapped.
       *
       * Firing it at creation buys the whole rest of the flow (test open, owner, share) as cover,
       * so the list is already reconciled by the time `finish()` navigates. Not awaited: the test
       * open is what this screen is for, and it must not wait on a list read. `finish()` fires a
       * second one for the installer who skips straight through.
       */
      void deviceCacheRef.current.refresh()
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setProvisionError(true)
      setProvisionPhase('provisionFailed')
    }
  }

  /**
   * Check 1 — the gate answers. Fired automatically the moment the gate exists, because the
   * installer has nothing to decide here: either the controller is reachable or the rest of the
   * screen is going to fail for a reason worth naming before the open button is offered.
   */
  function startStatusCheck(id: GateId): void {
    const run = statusRunRef.current + 1
    statusRunRef.current = run
    probeObservedRef.current = false
    setCheck('status', 'running')
    void deviceCacheRef.current.probeGate(id).finally(() => {
      // A superseded run's settlement is dropped rather than allowed to resolve a later one.
      if (statusRunRef.current === run) setStatusProbeSettled(run)
    })
  }

  // Fires `submitProvision()` once per arrival at `created` — guarded by a ref rather than a
  // one-shot flag in state, because StrictMode's double-invoked effect must see the same guard
  // both times. The cleanup also owns the confirm timers: it runs on every `step` change, which
  // covers "leaving testOpen" as well as unmount.
  useEffect(() => {
    // `gateId === undefined` is the second half of the guard and carries its own weight: a gate
    // that already exists must never be provisioned again from this screen. Re-running it would
    // hit D45's duplicate `409` — correct server behavior read as a dead end by the one installer
    // who is entitled to the gate, and the exact trap a back-then-forward produces.
    if (step === 'created' && gateId === undefined && !provisionStartedRef.current) {
      provisionStartedRef.current = true
      void submitProvision()
    }
    // The status check belongs to `testOpen`'s arrival rather than to provisioning's success: it
    // is the first row of a checklist the user is not looking at yet while `created` is on screen,
    // and a row that finished before its screen appeared is evidence with nowhere to land.
    if (step === 'testOpen' && gateId !== undefined && checks.status === 'idle') {
      startStatusCheck(gateId)
    }
    /**
     * The BLE check runs on arrival, for the same reason check 1 does: the installer has nothing
     * to decide here. The credential was typed on the previous screen and stored; either the
     * radio reaches the controller or it does not, and a tap in between only delays the answer.
     *
     * Guarded by a **ref**, not by `bleVerifyState === 'idle'` — `main.tsx` renders under
     * `StrictMode`, whose double-invoked mount effect sees the same pre-flush state twice and
     * would open two GATT sessions to one controller. The ref is cleared on any other step, so
     * the update flow's second pass through `ble → bleVerify` re-arms it.
     */
    if (step === 'bleVerify') {
      if (!bleVerifyStartedRef.current) {
        bleVerifyStartedRef.current = true
        void runBleVerify()
      }
    } else {
      bleVerifyStartedRef.current = false
    }
    return clearConfirmTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  /**
   * D267's miss path — a cold deep link to `/gates/:gateId/installation`, where nothing was
   * handed down and the address must be read before the name/location editor may open.
   *
   * The hit path issues nothing at all: `handedGate` has already seeded the name, the location
   * and `updateLoad: 'ready'` during render, which is what makes this screen paint complete on
   * its first frame. D176's own entry never reaches here — it calls `loadGateForUpdate` from
   * `enterUpdate` so that the retry button re-enters the same function.
   */
  useEffect(() => {
    if (updateFor === undefined || handedGate !== undefined) return
    if (updateReadStartedRef.current) return
    updateReadStartedRef.current = true
    void loadGateForUpdate(updateFor)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateFor])

  /**
   * Resolves check 1 from the device cache's own LED phase rather than from a value read after
   * `await probeGate(…)`. The distinction is not cosmetic: `probeGate` marks the gate `probing`
   * **synchronously** before it awaits the network, so `checking` is guaranteed to be observed
   * here, and the phase that follows it is the probe's real answer. A read taken straight after
   * the await depends on React having flushed that update first, which nothing promises.
   */
  useEffect(() => {
    if (checks.status !== 'running' || gateId === undefined) return
    const phase = deviceCache.statusPhase(gateId)
    if (phase === 'checking') {
      probeObservedRef.current = true
      return
    }
    if (!probeObservedRef.current && statusProbeSettled !== statusRunRef.current) return
    // `fresh` is the only phase reachable from a probe that answered `online` in this launch
    // (`statusPhaseOf`), so it is the only one that may pass. `offline` is a real red answer and
    // `stale` means the probe produced no usable evidence — both are the same verdict here.
    setCheck('status', phase === 'fresh' ? 'passed' : 'failed')
  }, [deviceCache, checks.status, gateId, statusProbeSettled, setCheck])

  function scheduleConfirm(idempotencyKey: string): void {
    clearConfirmTimers()
    // D140. Guarded here as well as at the call site so no future caller re-arms the sequence by
    // accident; everything below is unchanged and restored by the flag alone.
    if (!CONFIRM_POLLS_ENABLED) return
    const settle = (): boolean => {
      if (gateId === undefined) return false
      const seen = deviceCacheRef.current.livePosition(gateId)
      if (seen === undefined || !MOVED.has(seen)) return false
      clearConfirmTimers()
      setCheck('move', 'passed')
      return true
    }
    for (const delay of CONFIRM_POLLS_MS) {
      confirmTimers.current.push(
        window.setTimeout(() => {
          void (async () => {
            if (gateId === undefined || settle()) return
            await deviceCacheRef.current.probeGate(gateId, idempotencyKey)
            settle()
          })()
        }, delay),
      )
    }
    // The verdict is its own read-only tick after the last probe. Deciding it inside that probe's
    // own callback is what made the tail unreliable: the position it would have to read is set by
    // a React state update the callback has not necessarily seen yet, so a gate that answered on
    // the last poll could still be reported as unconfirmed.
    confirmTimers.current.push(
      window.setTimeout(() => {
        if (settle()) return
        // The whole allowance is spent with nothing moving — there is nothing further to wait
        // for, and D135 forbids reporting a move nobody has verified.
        setCheck('move', 'failed')
      }, CONFIRM_VERDICT_MS),
    )
  }

  async function runTestOpen(): Promise<void> {
    if (gateId === undefined || checks.open === 'running' || checks.move === 'running') return
    clearConfirmTimers()
    setChecks((prev) => ({ ...prev, open: 'running', move: 'idle' }))
    try {
      const result = await openGate(session.request, gateId, false)
      // D135: only a `2xx` carrying `outcome: 'opened'` (with a key to confirm against) is a
      // delivery receipt. Anything else — a refusal, a transport failure, a malformed response —
      // is reported as `failed` rather than left to imply progress that was never made. Note that
      // check 2 passing means only that: the command was accepted. Check 3 is the opening.
      if (result.ok && result.data.outcome === 'opened' && result.data.idempotencyKey !== undefined) {
        // D140: `skipped`, not `running`. Arming `move: 'running'` with no sequence behind it
        // would disable the open button (`opening` reads this row) and hold the checklist on a
        // spinner that nothing can ever settle.
        if (!CONFIRM_POLLS_ENABLED) {
          setChecks((prev) => ({ ...prev, open: 'passed', move: 'skipped' }))
          return
        }
        setChecks((prev) => ({ ...prev, open: 'passed', move: 'running' }))
        scheduleConfirm(result.data.idempotencyKey)
      } else {
        setCheck('open', 'failed')
      }
    } catch (error) {
      // R1-6. This used to rethrow *before* the reset below, so a non-`NetworkError` left
      // `checks.open` at `'running'` forever — the round open button disabled for the life of the
      // screen. The reset now runs unconditionally, and only then does a non-`NetworkError`
      // propagate.
      setCheck('open', 'failed')
      if (!(error instanceof NetworkError)) throw error
    }
  }

  /** Abandons whatever is still outstanding and moves on — available at every point (§7.8). */
  function skipTest(): void {
    clearConfirmTimers()
    setChecks((prev) => ({
      status: prev.status === 'running' || prev.status === 'idle' ? 'skipped' : prev.status,
      open: prev.open === 'running' || prev.open === 'idle' ? 'skipped' : prev.open,
      move: prev.move === 'running' || prev.move === 'idle' ? 'skipped' : prev.move,
    }))
    setStep('owner')
  }

  async function sendOwnerInvite(): Promise<void> {
    if (gateId === undefined || ownerBusy) return
    setOwnerBusy(true)
    setOwnerError(undefined)
    try {
      const result = await inviteMember(session.request, gateId, ownerPhone, locale, ownerRole)
      if (!result.ok) {
        setOwnerError(result.status === 422 ? 'invalid' : 'generic')
        return
      }
      setInvite(result.data)
      setStep('share')
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setOwnerError('generic')
    } finally {
      setOwnerBusy(false)
    }
  }

  function finish(): void {
    // The backstop for `submitProvision()`'s refresh: an installer who reached `done` faster than
    // that request came back, or whose reception dropped in between. Fired and not awaited (same
    // reasoning as `GateDetailScreen`'s post-delete refresh) — Home renders the cached list and
    // `refreshing` is what tells the user the list is still settling.
    void deviceCache.refresh()
    navigate.resetTo({ id: 'home' })
  }

  /**
   * D147. No gate was ever provisioned on this attempt (`gateId` is still `undefined` whenever
   * `conflict` is reached), so this is the opposite case from D138's guard — that guard removes
   * the back affordance once a gate *exists*, and stays exactly as it is. Only the code is
   * cleared: the installer typed a real name and location for a real gate, and a mistyped or
   * mis-stickered code is the one thing worth asking them to redo.
   */
  function startOver(): void {
    provisionStartedRef.current = false
    setProvisionConflict(undefined)
    setConflictUnparseable(false)
    setConflictOrigin('naming')
    setCode('')
    setCodeError(undefined)
    setStep('code')
  }

  /**
   * **Wrapped in `.form-row`, exactly as `PhoneStep`'s "send code" button is.** `.btn-primary` and
   * `.btn-secondary` are `width: 100%` with no inset of their own, so a bare one runs edge to edge
   * while every other element on these screens — inputs, hints, chips, the checklist — sits at the
   * same 16px inline inset. That mismatch is what made the install's buttons wider than the one
   * the auth flow shows for the same kind of action.
   */
  const footer = (
    label: string,
    onClick: () => void,
    disabled = false,
    secondary = false,
    // Optional, and **only where the glyph names the action rather than decorating it** — a
    // screen whose buttons all carry an icon has said nothing more than one whose buttons carry
    // none. `.btn-with-icon` is a box-only class (`global.css`) that composes with either
    // variant; omitting the icon renders byte-identical markup to what this helper always did.
    icon?: IconName,
    // Marks the one button per step that Enter should reach — same shape as the auth flow's
    // steps (`PhoneStep`/`CodeStep`/`NameStep`). `type="submit"` inside the step's own `<form>`
    // is what gets Enter there; `onClick` is deliberately left off in that case so the action
    // runs once, from the form's `onSubmit`, rather than twice (click + submit both firing it).
    submitType = false,
  ): JSX.Element => (
    <div className="form-row">
      <button
        className={`${secondary ? 'btn-secondary' : 'btn-primary'}${icon === undefined ? '' : ' btn-with-icon'}`}
        type={submitType ? 'submit' : 'button'}
        disabled={disabled}
        onClick={submitType ? undefined : onClick}
      >
        {icon === undefined ? null : <Icon name={icon} size={16} />}
        {label}
      </button>
    </div>
  )

  /**
   * **R7/D279 — the close-mode pill, one renderer for both surfaces that draw it.**
   *
   * `.scheme-segmented`/`.scheme-segment` are reused from `PreferencesScreen` exactly as D245's
   * invite segments are — a third segmented treatment would be a third thing to keep in step with
   * the other two — and the group is a `<div role="group">` of `aria-pressed` toggle buttons
   * rather than a radiogroup, which is that control's own recorded reason (a radiogroup owes
   * arrow-key roving focus two adjacent buttons do not).
   *
   * The two callers differ only in what a tap *does*: on the install step it changes local state
   * and sends nothing (D245's rule for the invite pill — selecting a segment saves nothing and
   * opens nothing), and on the update menu it writes. The segments carry **no glyph**: the pair
   * here is two words rather than three, and a mark beside each would say nothing the label does
   * not.
   */
  const closeModePill = (onChoose: (mode: CloseMode) => void, disabled = false): JSX.Element => (
    <div className="form-row">
      <div className="scheme-segmented" role="group" aria-label={t('gateTypeTitle')}>
        {CLOSE_MODE_OPTIONS.map((option) => (
          <button
            key={option}
            className={`scheme-segment${option === closeMode ? ' selected' : ''}`}
            type="button"
            aria-pressed={option === closeMode}
            disabled={disabled}
            onClick={() => onChoose(option)}
          >
            <span>{t(CLOSE_MODE_LABELS[option])}</span>
          </button>
        ))}
      </div>
    </div>
  )

  /**
   * Enter-to-continue for a step that has no `<form>` to submit.
   *
   * The BLE step is the one step deliberately left formless (see its own comment): its two fields
   * are a credential pair to any autofill service, and a `<form>` around them is the shape that
   * triggers fill on both and a save prompt on submit. Implicit submission goes with the form, so
   * the behaviour c456ec5 added is carried here instead — same key, same action, no form.
   */
  const onEnterSubmit =
    (run: () => void) =>
    (event: KeyboardEvent<HTMLInputElement>): void => {
      // `isComposing` guards an IME: Enter commits a candidate there and must not also submit.
      if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
      event.preventDefault()
      run()
    }

  if (step === 'code') {
    const codeSubmitDisabled = code.length !== 6 || codeError !== undefined || codeCheckBusy
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!codeSubmitDisabled) void continueFromCode()
        }}
      >
        <AppBar
          title={t('title')}
          onBack={() => {
            stopScanner()
            navigate.back()
          }}
        />
        <div className="section-title">{t('codeTitle')}</div>
        <div className="filter-row" role="group" aria-label={t('codeTitle')}>
          <button
            className={`filter-chip${codeMethod === 'qr' ? ' on' : ''}`}
            type="button"
            aria-pressed={codeMethod === 'qr'}
            onClick={() => selectCodeMethod('qr')}
          >
            {t('methodQr')}
          </button>
          <button
            className={`filter-chip${codeMethod === 'manual' ? ' on' : ''}`}
            type="button"
            aria-pressed={codeMethod === 'manual'}
            onClick={() => selectCodeMethod('manual')}
          >
            {t('methodManual')}
          </button>
        </div>
        {codeMethod === 'manual' ? (
          <div className="form-row">
            <label htmlFor="installGateCode">{t('codeFieldLabel')}</label>
            <input
              id="installGateCode"
              className="input mono gate-code-input"
              value={code}
              maxLength={6}
              /**
               * D131 — **`none`, and no `.toUpperCase()`.** The code becomes an MQTT topic
               * verbatim and topic names are case-sensitive, so changing its case here points
               * the install at a topic no device listens on: it provisions cleanly and the gate
               * reads offline forever. `characters` was the mobile half of the same bug — iOS
               * would re-capitalize a lower-case sticker code even with the handler fixed.
               */
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={codeError !== undefined}
              onChange={(e) => {
                setCode(e.target.value)
                setCodeError(undefined)
              }}
            />
            <p
              className="hint gate-code-hint"
              style={codeError !== undefined ? { color: 'var(--danger-text)' } : undefined}
            >
              {codeError === 'format'
                ? t('codeInvalid')
                : codeError === 'claim'
                  ? t('codeUnknownOrFailed')
                  : t('codeHelp')}
            </p>
          </div>
        ) : (
          <div className="qr-reader">
            {/*
              A2 — **never unmount this.** `startScanning()` reads `videoRef.current` and bails
              to `reader-failed` if it is `null`, so the viewfinder stays mounted through every
              status and is only ever *hidden* with a class — unmounting it here would break
              every scan, including the very first auto-started one.
            */}
            <div
              className={`qr-viewfinder${qrStatus === 'scanning' ? ' active' : ''}${
                qrStatus === 'requesting' || qrStatus === 'scanning' ? '' : ' qr-viewfinder-hidden'
              }`}
            >
              <video ref={videoRef} muted playsInline autoPlay aria-label={t('qrViewfinderLabel')} />
              {qrStatus !== 'scanning' ? (
                <div className="qr-viewfinder-label">
                  <Icon name="scan-qr-code" size={28} />
                  <span>{t('qrScanAction')}</span>
                </div>
              ) : null}
            </div>
            {/*
              The torch, directly beneath the viewfinder and above the copy. Rendered only while
              video is live *and* the track says it has a lamp — a control that is present and
              does nothing is worse than one that is absent, and every desktop and most front
              cameras have no `torch` capability at all.

              **Below the frame rather than over it.** Inside, it covered the bottom of the very
              image the installer is aiming, and a QR sticker is often the full height of the
              square; a control that hides part of the target to light it is working against
              itself. Out here it is also read against the page rather than against live video,
              which is what lets it use palette tokens instead of a scrim.

              `aria-pressed` carries the state and the glyph does not change, so the lit and unlit
              pill are one control rather than two.
            */}
            {qrStatus === 'scanning' && torchAvailable ? (
              <button
                className={`qr-torch${torchOn ? ' on' : ''}`}
                type="button"
                aria-pressed={torchOn}
                onClick={() => void toggleTorch()}
              >
                <Icon name="flashlight" size={16} />
                <span>{t(torchOn ? 'qrTorchOff' : 'qrTorchOn')}</span>
              </button>
            ) : null}
            {/* A2 — idle: no viewfinder square, a big Scan button instead. Also covers the
                (rare, user-driven) case of selecting QR manually when the camera is unsupported
                — the tap resolves straight to `unsupported` below rather than hanging. */}
            {qrStatus === 'idle' ? (
              <div className="qr-scan-panel">
                <button className="qr-scan-btn" type="button" onClick={() => void startScanning()}>
                  <Icon name="scan-qr-code" size={28} />
                  <span>{t('qrScanAction')}</span>
                </button>
              </div>
            ) : null}
            {/*
              **D73 §B2a — the just-in-time camera purpose string, and it is not decoration.**
              `install.qrRationale` is a compliance artifact: `docs/compliance/d73-engagement.md`
              reproduces it verbatim *"so the notice set and the product cannot drift"*, and its
              stated posture is that it is shown on the code step **before the browser prompt is
              invoked**. It used to sit behind the `qrStart` tap, which is what put it in front of
              the prompt; A1's auto-start removed that tap and, with it, the notice.
              Rendering it unconditionally for every pre-capture state is what restores the
              posture — React commits this paint *before* the mount effect fires `getUserMedia`,
              so the string is on screen when the prompt appears. **Do not gate this on a tap, a
              status, or a timer**, and do not delete the key: an unmirrored change to that notice
              set is a defect (AGENTS.md §4.3). The other two constraints B2a names are held
              elsewhere — *manual entry is always available* by the method chips above, and *never
              re-prompt automatically after a denial* by `autoStartedRef`.
            */}
            {cameraAvailable && qrStatus !== 'scanned' ? (
              <p className="qr-rationale">{t('qrRationale')}</p>
            ) : null}
            {/* A3 — captured: the code, large and mono, plus the sticker-check line and a
                re-scan control. A wrong capture must be recoverable. */}
            {qrStatus === 'scanned' ? (
              <div className="qr-result">
                <div className="qr-result-code mono">
                  <Bidi>{code}</Bidi>
                </div>
                <div className="qr-result-hint">{t('qrStickerConfirm')}</div>
                <button className="btn-secondary" type="button" onClick={() => void startScanning()}>
                  {t('qrRetry')}
                </button>
              </div>
            ) : null}
            {qrStatus === 'requesting' || qrStatus === 'scanning' ? (
              <div className="qr-status" aria-live="polite" role="status">
                {qrStatus === 'requesting' ? t('qrRequesting') : t('qrScanning')}
              </div>
            ) : null}
            {qrStatus === 'scanning' || qrStatus === 'requesting' ? (
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  stopScanner()
                  setQrStatus('idle')
                }}
              >
                {t('qrCancel')}
              </button>
            ) : null}
            {/* Unsupported is a dead end, not a retry-able failure — no camera API exists at
                all, so there is no button offering to try again. */}
            {qrStatus === 'unsupported' ? (
              <div className="qr-status" aria-live="polite" role="status">
                {t('qrUnsupported')}
              </div>
            ) : null}
            {qrStatus === 'invalid' ||
            qrStatus === 'denied' ||
            qrStatus === 'unavailable' ||
            qrStatus === 'in-use' ||
            qrStatus === 'failed' ||
            qrStatus === 'reader-failed' ? (
              <>
                <div className="qr-status" aria-live="polite" role="status">
                  {qrStatus === 'invalid' ? t('qrInvalid') : null}
                  {qrStatus === 'denied' ? t('qrDenied') : null}
                  {qrStatus === 'unavailable' ? t('qrUnavailable') : null}
                  {qrStatus === 'in-use' ? t('qrInUse') : null}
                  {qrStatus === 'failed' || qrStatus === 'reader-failed' ? t('qrFailed') : null}
                </div>
                <button className="btn-secondary" type="button" onClick={() => void startScanning()}>
                  {t('qrRetry')}
                </button>
              </>
            ) : null}
          </div>
        )}
        {footer(
          codeCheckBusy ? t('codeChecking') : t('continue'),
          () => void continueFromCode(),
          codeSubmitDisabled,
          false,
          undefined,
          true,
        )}
      </form>
    )
  }

  if (step === 'naming') {
    // D176. One screen, two exits. The title and the button change because "continue" is a lie on
    // a screen that saves and returns, and the address picker, the 60-character name and both
    // validations are identical — which is the whole reason this reuses the step rather than
    // growing a second one.
    const updating = mode === 'update'
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!updateBusy) continueFromNaming()
        }}
      >
        <AppBar
          title={updating ? t('updateLocationTitle') : t('defaultNameLabel')}
          onBack={() => setStep(updating ? 'updateMenu' : 'code')}
        />
        <div className="form-row">
          <label htmlFor="installName">{t('defaultNameLabel')}</label>
          <input
            id="installName"
            className="input"
            value={defaultName}
            maxLength={60}
            onChange={(e) => setDefaultName(e.target.value)}
          />
        </div>
        <div className="form-row">
          <label htmlFor="installLocation">{t('locationLabel')}</label>
          {/*
            P17. The same field P7 shipped, with D51's other two inputs attached to it. The
            picker's own copy is the `location` namespace's; `locationLabel`/`locationRequired`
            stay here because the label and the caller's own validation message are this screen's.
          */}
          <LocationPicker
            inputId="installLocation"
            value={location}
            onChange={setLocation}
            {...(namingError === 'location' ? { error: t('locationRequired') } : {})}
          />
        </div>
        {namingError === 'name' ? <p className="hint error">{t('nameRequired')}</p> : null}
        {updateError !== undefined ? (
          <p className="hint error">{t(updateError === 'network' ? 'updateNetwork' : 'updateFailed')}</p>
        ) : null}
        {footer(
          updating ? (updateBusy ? t('updateSaving') : t('updateSave')) : t('continue'),
          continueFromNaming,
          updateBusy,
          false,
          undefined,
          true,
        )}
      </form>
    )
  }

  /**
   * **R7/D279 — the gate-type step. It asks about the CONTROLLER, and it says so.**
   *
   * The footnote is not decoration: the behaviour is set in the controller by the technician, and
   * this screen only reports it to the app. An installer who reads the two cards as a *choice*
   * about what the gate will now do would tick the wrong one on a gate whose hardware disagrees,
   * and the app would then draw a Close button that opens nothing.
   *
   * **`המשך` is an in-flow `footer()` button, never a pinned `.screen-footer`.** Every step of
   * this flow renders its primary action through that helper inside a `.form-row`; D245's sticky
   * footer belongs to invite, the address book and the access list, and this flow has never used
   * one. **And there is no skip** — the step collects a value the provision body carries, and a
   * skip would be a second way of answering `auto` that reads as *not answered*.
   *
   * Back goes to `naming` and nothing else: this step is unreachable in `update` mode, where
   * `naming`'s continue saves and returns to the menu rather than coming here.
   */
  if (step === 'gateType') {
    return (
      <>
        <AppBar title={t('gateTypeTitle')} onBack={() => setStep('naming')} />
        <p className="hint">{t('gateTypeHint')}</p>
        {closeModePill(setCloseMode)}
        {/*
          Both cards render always, and only the current one is marked. A screen that showed the
          selected explainer alone would make the pill a control whose consequence is invisible
          until it is operated, which is the opposite of what an explainer is for.
        */}
        <div className="gate-type-cards">
          {CLOSE_MODE_OPTIONS.map((option) => (
            <div key={option} className={`gate-type-card${option === closeMode ? ' current' : ''}`}>
              <span className="gate-type-card-head">{t(CLOSE_MODE_LABELS[option])}</span>
              <span className="gate-type-card-line">{t(CLOSE_MODE_BODY[option])}</span>
              <span className="gate-type-card-line">{t(CLOSE_MODE_BODY_APP[option])}</span>
            </div>
          ))}
        </div>
        <p className="hint">{t('gateTypeSource')}</p>
        {footer(t('continue'), () => setStep('created'))}
      </>
    )
  }

  if (step === 'conflict' && (provisionConflict !== undefined || conflictUnparseable)) {
    return (
      <>
        <AppBar title={t('duplicateTitle')} onBack={() => setStep(conflictOrigin)} />
        <div className="danger-card">
          <div className="danger-title">{t('duplicateTitle')}</div>
          <p>
            {provisionConflict !== undefined
              ? t('duplicateBody', { name: provisionConflict.defaultName })
              : t('duplicateBodyUnknown')}
          </p>
          {/*
            D147. "Open existing" only renders when the projection set `viewer` — i.e. only when
            the installer is already a member of the gate that holds this code — which is the one
            case D45's duplicate `409` genuinely has a forward path today. "Start over" is the
            escape for every other case: someone else's gate, and a `409` this client could not
            even parse into a named conflict.
          */}
          {/*
            **All three actions carry the same `.btn-secondary` treatment — this screen has no
            primary.** It is a refusal, and the three ways out of it are a genuine choice rather
            than one recommendation with two escapes: which is right depends on whether the
            installer scanned the sticker they meant to. Promoting *update* would push a manager
            toward editing a gate they may have reached by mistyping a code. Do not "restore"
            a primary here for visual hierarchy — the flatness is the hierarchy.

            D176. A manager of the gate holding this code is the one person for whom "already
            installed" is not a dead end: both edits behind this button are manager-only routes
            they could reach anyway, one gate screen at a time. The test is **`role` alone, with
            no `status` clause**, because that is exactly what `requireManager` checks server-side
            (`provision/routes.ts`) — a stricter client would hide a button on a gate the API
            would happily let them edit, which is a worse answer than the one the server gives.
          */}
          {provisionConflict?.viewer?.role === 'manager'
            ? footer(t('updateOffer'), () => enterUpdate(provisionConflict), false, true, 'pencil')
            : null}
          {/*
            `door-open`, matching the label — פתיחת השער הקיים / "open the existing gate".
            **Note that this button navigates and actuates nothing**, so the glyph it shares with
            Gate Detail's own open control is doing double duty. If this screen ever grows a
            control that *does* send a command, these two must not look alike.
          */}
          {provisionConflict?.viewer !== undefined
            ? footer(
                t('openExisting'),
                () => navigate.navigate({ id: 'gate', gateId: provisionConflict.gateId as GateId }),
                false,
                true,
                'door-open',
              )
            : null}
          {footer(t('startOver'), startOver, false, true, 'rotate-ccw')}
        </div>
      </>
    )
  }

  /**
   * D176's menu. Three actions and no state of its own beyond what the two editors left behind.
   *
   * **Back goes to `conflict`, not to `code`** — on D176's entry. The manager arrived by scanning
   * a code that is already claimed; the screen that says so is the true previous step, and it
   * still holds the "start over" escape for a manager who realises they scanned the wrong
   * sticker. On D267's `installEdit` entry there is no scan behind this menu at all, so back is
   * the router's own — the Access page the manager came from.
   */
  if (step === 'updateMenu' && updateGateId !== undefined) {
    return (
      <>
        <AppBar
          title={t('updateMenuTitle')}
          onBack={() => (updateOrigin === 'route' ? navigate.back() : setStep('conflict'))}
        />
        {/*
          **The line asserts two things and is withheld unless both are true.** It reads *"You
          manage {{name}}"*, which on D176's entry is guaranteed — the offer that reaches this
          branch is gated on `role === 'manager'` — and on D267's is guaranteed by neither half
          until the record has been read.

          With no name it renders "You manage ." on a cold deep link's first frame, a sentence
          about a gate it cannot name; `updateLoading` below carries that window instead. With a
          **confirmed resident** it renders "You manage חניון" directly above the line saying they
          do not, which is a screen contradicting itself — found on a resident deep link in the
          browser, not by reading this code. The refusal below is what speaks for that case.
        */}
        {defaultName === '' || updateRole === 'resident' ? null : (
          <p className="hint">{t('updateMenuHelp', { name: defaultName })}</p>
        )}
        {/*
          D267 — a **confirmed** resident is refused here rather than in front of a `403`.
          The row this screen opens from is manager-only (Gate Detail renders it on `manager`
          alone), so the only way to arrive without the role is a typed URL. The test is `role`
          and nothing else, which is D176 (b): `requireManager` has no `status` clause, so a
          client demanding `active` too would hide the action on a gate the API would let this
          manager edit. An **unknown** role is not a refusal — that is D153's fail-open rule for
          the same class of absent field — and both writes are still `requireManager` server-side
          whatever this renders.
        */}
        {updateRole === 'resident' ? <p className="hint error">{t('updateNotManager')}</p> : null}
        {updateSaved ? (
          <p className="hint" role="status" aria-live="polite">
            {t('updateSaved')}
          </p>
        ) : null}
        {/*
          **R7/D279 — the pill's failure reports through the strings the naming editor already
          has**, `updateFailed`/`updateNetwork`, rather than through a sentence of its own. A
          per-field failure line would be a new key in five catalogs saying what the generic one
          already says, and a save that failed has one thing to report whichever field it was.
        */}
        {updateError !== undefined ? (
          <p className="hint error">{t(updateError === 'network' ? 'updateNetwork' : 'updateFailed')}</p>
        ) : null}
        {/* **The two edits are unordered and look it; *done* is the one that does not.** A
            technician who came to re-pair a radio is not doing a lesser version of a rename, so
            neither edit outranks the other — they share a treatment and are told apart by their
            glyphs. The fill on *done* separates *leave* from *edit*, which is the only ranking
            this screen actually has. */}
        {updateLoad === 'loading' ? <p className="hint">{t('updateLoading')}</p> : null}
        {/*
          **R7/D279 — the close-mode pill, above the two edit rows and absent unless the record
          was read.** It is pre-filled from `updateLoad`'s record and from nothing else, so while
          that read is in flight or has failed there is nothing to pre-fill it with: the existing
          `updateLoadFailed` line and its retry are what the manager gets instead, exactly as the
          name/location row is gated on the same read.

          Disabled for a **confirmed** resident on the two rows' own rule (D267/D176 (b)): the
          test is `role` alone, an unknown role is not a refusal, and the write is
          `requireManager` server-side whatever this renders.
        */}
        {updateLoad === 'ready'
          ? closeModePill((next) => void saveCloseMode(next), updateBusy || updateRole === 'resident')
          : null}
        {updateLoad === 'failed' ? (
          <>
            <p className="hint error">{t('updateLoadFailed')}</p>
            {footer(
              t('updateLoadRetry'),
              () => void loadGateForUpdate(updateGateId),
              false,
              true,
              'refresh-cw',
            )}
          </>
        ) : null}
        {footer(
          t('updateLocationAction'),
          () => {
            setUpdateSaved(false)
            setUpdateError(undefined)
            setNamingError(undefined)
            setStep('naming')
          },
          updateLoad !== 'ready' || updateRole === 'resident',
          true,
          // The location half is what distinguishes this action from the one below it — both
          // "update", only one a place — so the pin carries the difference where a pencil would
          // just repeat the verb.
          'map-pin',
        )}
        {/*
          Never gated on `updateLoad`: this step pre-fills nothing, because there is nothing to
          pre-fill. `PUT /gates/{id}/ble-credentials` stores a KMS-encrypted password and returns
          `bleConfigured` — the plaintext has no path back to any client by design (D85/D94), so
          an update is a replacement of both fields rather than an edit of either, and it works
          perfectly well on a gate whose record this screen never managed to read.
        */}
        {footer(
          t('updateBleAction'),
          () => {
            setUpdateSaved(false)
            setBleError(undefined)
            setStep('ble')
          },
          updateRole === 'resident',
          true,
          'bluetooth',
        )}
        {/* **Primary, and the only button on either screen without a glyph.** It is the one action
            that ends the visit rather than opening another screen, and it is not an *edit* — an
            icon beside it would put it in the same family as the two rows above and imply a third
            thing to change. The fill is what separates leaving from editing. */}
        {footer(t('updateDone'), finishUpdate)}
      </>
    )
  }

  if (step === 'created') {
    return (
      <>
        {/* Back exists only while there is something to go back *to*. Once the gate is
            provisioned, `naming` no longer edits anything — a rename there would never be sent —
            so the bar drops the affordance rather than offering a step that silently does
            nothing. Renaming afterwards is Device settings' (§7.5). With no back and no menu the
            bar is title-only, rather than growing a hamburger that leads nowhere. */}
        <AppBar
          title={t('createdBarTitle')}
          {...(gateId === undefined
            ? {
                onBack: () => {
                  provisionStartedRef.current = false
                  setStep('naming')
                },
              }
            : {})}
        />
        {provisionPhase === 'provisioning' ? (
          <div className="hint centered" style={{ padding: '36px 10px' }} role="status" aria-live="polite">
            {t('provisioningInProgress')}
          </div>
        ) : null}
        {provisionPhase === 'provisionFailed' ? (
          <>
            <p className="hint error centered">{t('provisionFailed')}</p>
            {footer(t('testOpenRetry'), () => void submitProvision())}
          </>
        ) : null}
        {provisionPhase === 'ready' && gateId !== undefined ? (
          <>
            <div className="install-created">
              <span className="install-created-mark" aria-hidden="true">
                <Icon name="check" size={26} />
              </span>
              <h2 className="install-created-title">{t('createdTitle')}</h2>
              <div className="install-created-id-label">{t('createdIdLabel')}</div>
              {/* `formatGateId`, and `dir="ltr"` inside a `<bdi>` — the same treatment Gate Detail
                  gives it. An eight-digit number rendered without it reorders in this RTL shell. */}
              <bdi className="install-created-id" dir="ltr">
                {formatGateId(gateId)}
              </bdi>
            </div>
            {/* §7.8's order: credentials (step 4) before the test open (step 5). */}
            {footer(t('continue'), () => setStep('ble'))}
          </>
        ) : null}
      </>
    )
  }

  if (step === 'ble') {
    return (
      /*
        **This step is deliberately NOT a `<form>`, alone among the steps c456ec5 converted.**
        A form holding one plain text field and one masked one, submitted and then navigated away
        from, *is* the shape of a sign-in — and that shape is what every credential manager keys
        on, ahead of any attribute. It is why fill was being offered on **both** fields (the pair
        read as username + password) and why a save prompt followed the submit. No amount of
        `data-*` fixes this: those are **browser-extension** conventions, and a phone's autofill
        is an **OS service** (Android's Autofill Framework, iOS AutoFill) reading the field tree,
        not the DOM's opt-out attributes.

        Enter-to-continue survives without it — `onEnterSubmit` below is on both inputs — so what
        is lost is the implicit-submission semantics and nothing a user can see. **Do not
        "restore consistency" by wrapping this step back in a `<form>`**: it is the one step whose
        two fields can be read as a credential pair, and consistency with the other steps is what
        this trades away on purpose.
      */
      <div>
        {/* No back **during an install**: the gate exists, and the step behind this one edits
            nothing that is still being sent. Same reasoning `created` gives for dropping the
            affordance. D176's update branch is the opposite case — the menu behind it is a real
            place to return to, and returning changes nothing — so there the affordance is back. */}
        <AppBar
          title={mode === 'update' ? t('updateBleTitle') : t('bleTitle')}
          {...(mode === 'update' ? { onBack: () => setStep('updateMenu') } : {})}
        />
        <p className="hint">{mode === 'update' ? t('updateBleHelp') : t('bleHelp')}</p>
        <div className="form-row">
          <label htmlFor="installBleName">{t('bleNameLabel')}</label>
          {/*
            The controller's advertised name, and — sitting in the same `<form>` as the secret
            below — the field a login heuristic would read as the *username* half of a pair. The
            pairing is broken by what the secret field is (see its comment), not by disguising
            this one, so this stays an ordinary text input; what it must not do is invite a fill.
            `autoComplete="off"` is the whole of that, and it is easy to leave off by accident:
            it was.
          */}
          <input
            id="installBleName"
            className="input"
            value={bleName}
            maxLength={40}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={onEnterSubmit(() => {
              if (!bleBusy) void submitBleCredentials()
            })}
            onChange={(e) => {
              setBleName(e.target.value)
              setBleError(undefined)
            }}
          />
        </div>
        <div className="form-row">
          <label htmlFor="installBleSecret">{t('bleCodeLabel')}</label>
          {/*
            **Masked, and invisible to every credential manager we can name.** This is a per-gate
            controller secret, not an account one: saving it to a phone's password vault files it
            under the app's own origin, offers it back on the *next* gate's install, and puts a
            value D85 accepts as plaintext-in-transit into a store D85 never reasoned about.

            Three separate layers of detection have to be answered, and `type` alone answers only
            the first:
              1. **`type="password"`** is what Chrome's save prompt requires, so the field is
                 `text` **always** and the eye toggle flips the `.text-masked` CSS class
                 (`global.css`) instead. Never make this conditional again.
              2. **Every string the element carries.** Autofill engines regex `id`, `name`
                 **and `class`** for `password|passwort|pwd|pass|…`, so the id is
                 `installBleSecret`, there is no `name` at all, and the wrapper/toggle classes
                 are `secret-field`/`secret-toggle` — a class attribute is in the DOM like any
                 other. Fewer strings to match is the whole technique. **Do not rename any of
                 them back toward "password"**, which is what all three were.
              3. **Per-vendor opt-outs**, because `autocomplete="off"` is advisory and every
                 manager below ignores it. These are the documented attributes, one per vendor;
                 an unknown `data-*` costs nothing where the extension is absent.

            **The visible copy is the fourth layer and it took a product decision to close.** The
            vendor calls this a password and the field said `סיסמת השער`, which is the string a
            label-matching heuristic reads — and a label is the one signal no attribute can hide.
            It now reads **קוד הבקר** / *Controller code* throughout the BLE step, which is also
            why it is the *controller's* code rather than the *gate's*: step 1 already asks for
            `הקוד המודפס על השער`, and two different values both called "the gate's code" in one
            flow is a worse defect than the one this fixes. Nothing here reaches a store, a log,
            or analytics either way (see the state declaration above).
          */}
          <div className="secret-field">
            <input
              id="installBleSecret"
              className={`input${showBlePassword ? '' : ' text-masked'}`}
              type="text"
              value={blePassword}
              maxLength={40}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              data-1p-ignore=""
              data-lpignore="true"
              data-bwignore=""
              data-protonpass-ignore="true"
              data-form-type="other"
              onKeyDown={onEnterSubmit(() => {
                if (!bleBusy) void submitBleCredentials()
              })}
              onChange={(e) => {
                setBlePassword(e.target.value)
                setBleError(undefined)
              }}
            />
            <button
              className="secret-toggle"
              type="button"
              onClick={() => setShowBlePassword((shown) => !shown)}
              aria-label={t(showBlePassword ? 'bleCodeHide' : 'bleCodeShow')}
            >
              <Icon name={showBlePassword ? 'eye-off' : 'eye'} size={18} />
            </button>
          </div>
        </div>
        {bleError !== undefined ? <p className="hint error">{t(BLE_ERROR_KEYS[bleError])}</p> : null}
        {/* `submitType` stays false: there is no form for a submit button to belong to, and
            Enter is carried by the inputs' own handler instead. */}
        {footer(bleBusy ? t('bleSaving') : t('continue'), () => void submitBleCredentials(), bleBusy)}
        {/*
          **Skippable, and the flow does not block on it.** A gate with no BLE credentials is a
          perfectly good gate — residents open over cloud, the primary path since D4 — and a
          technician who cannot get the radio working should finish the install rather than
          abandon it. The test-open step already takes exactly this posture.
        */}
        {mode === 'update'
          ? footer(t('updateCancel'), () => setStep('updateMenu'), bleBusy, true)
          : footer(t('bleSkip'), () => setStep('testOpen'), bleBusy, true)}
      </div>
    )
  }

  if (step === 'bleVerify') {
    return (
      <>
        <AppBar title={t('bleVerifyTitle')} />
        <p className="hint">{t('bleVerifyHelp')}</p>
        <ol className="install-checks" aria-live="polite">
          <CheckRow
            state={bleVerifyState}
            label={t('bleVerifyRow')}
            detail={t(
              bleVerifyState === 'failed' && bleVerifyReason !== undefined
                ? BLE_VERIFY_FAILURE_KEYS[bleVerifyReason]
                : BLE_VERIFY_KEYS[bleVerifyState],
            )}
          />
        </ol>
        {/*
          **No "check now".** The check starts on arrival (see the `step` effect), so `idle` is a
          single frame before `running` and a button offering to begin something already begun is
          a control with nothing to do. `failed` keeps its retry: that one is a real decision —
          switch Bluetooth on, walk closer, and try the same credential again.
        */}
        {bleVerifyState === 'failed' ? footer(t('testOpenRetry'), () => void runBleVerify()) : null}
        {/*
          **Continue is offered whatever the row says, and that is deliberate.** The credential is
          already stored; a failed verification means the *radio* did not answer here and now — a
          phone with Bluetooth off, an iOS browser with no Web Bluetooth at all, a controller out
          of range — none of which is a reason to strand a technician mid-install. What the row
          must not do is claim a pass it did not get, and it does not.
        */}
        {/* D176: the verification itself is unchanged — same radio, same `STA:*?`, same refusal to
            claim a pass it did not get. Only where "continue" lands differs, because an update has
            no test open behind it and no install left to finish. */}
        {mode === 'update'
          ? footer(
              t('continue'),
              () => {
                setUpdateSaved(true)
                setStep('updateMenu')
              },
              bleVerifyState === 'idle' || bleVerifyState === 'running',
              true,
            )
          : footer(
              t('continue'),
              () => setStep('testOpen'),
              bleVerifyState === 'idle' || bleVerifyState === 'running',
              true,
            )}
      </>
    )
  }

  if (step === 'testOpen') {
    /**
     * **The open tile is inactive until check 1 has passed.** This reverses the earlier posture
     * — which offered the open on *any* answer, on the argument that an unanswered probe and a
     * dead controller look identical from here — and the reason is the notice below: the most
     * common cause of a failed probe on a fresh install is the controller still being held by
     * the SEAV app, which the installer must go and release. Offering an open in that state
     * produces a failure that names nothing; the notice names the actual repair, and check 1's
     * own retry is the way back. `skipTest` still leaves at any point (§7.8).
     */
    const canOpen = checks.status === 'passed'
    const opening = checks.open === 'running' || checks.move === 'running'
    const confirmed = checks.move === 'passed'
    /**
     * The button says *try again* rather than *test open* once something has been refused. It
     * stays offered after a pass too: D140's copy asks the installer to confirm with their own
     * eyes, and a gate they watched not move needs a second command — the retry sends a real
     * open, so it is not the dead retry D140 removed with the movement verdict.
     */
    const finishedTrying = checks.open === 'failed' || checks.move === 'failed'
    /**
     * D140: what earns the *Continue* footer. Normally movement, verified. With the confirm
     * sequence off there is no such evidence to be had, so an accepted command is what the flow
     * proceeds on — and `unverified` is what makes the copy below say so rather than borrowing
     * `testOpenSuccess`, which claims the gate moved.
     */
    const unverified = !CONFIRM_POLLS_ENABLED && checks.open === 'passed' && checks.move === 'skipped'
    const mayContinue = confirmed || unverified
    return (
      <>
        {/* No back and no menu: the gate exists, so there is nothing behind this step, and the
            title-only bar is what keeps a dead hamburger off a screen that must not be left. */}
        <AppBar title={t('testOpenTitle')} />
        <p className="hint">{t('testOpenIntro')}</p>
        <ol className="install-checks" aria-live="polite">
          <CheckRow
            state={checks.status}
            label={t('checkStatusLabel')}
            detail={t(
              checks.status === 'running'
                ? 'checkStatusRunning'
                : checks.status === 'passed'
                  ? 'checkStatusPassed'
                  : checks.status === 'failed'
                    ? 'checkStatusFailed'
                    : checks.status === 'skipped'
                      ? 'checkSkipped'
                      : 'checkPending',
            )}
            {...(checks.status === 'failed' && gateId !== undefined
              ? { onRetry: () => startStatusCheck(gateId), retryLabel: t('testOpenRetry') }
              : {})}
          />
          {/*
            **The action, above the row that reports it.** The round control this replaces was a
            second *object* for one subject — a 180px disc parked beside a checklist — but folding
            it into the row went one step too far the other way: one box cannot be both the
            invitation and the verdict without its copy meaning two things at once. So the button
            is its own tile, green because it is the deliberate action on the screen, and it sits
            **directly above** check 2, which is what ties the two back together without merging
            them. Greyed until check 1 passes; see `canOpen`.

            The move row is **not rendered**, and that is a rendering change only: D140 leaves
            `checks.move` and its whole state machine in place (`unverified` below is read from
            it), because restoring `CONFIRM_POLLS_ENABLED` must restore a row that reports. What
            it must not do meanwhile is occupy a line to say it was never run.
          */}
          <TestOpenTile
            label={t(finishedTrying ? 'testOpenRetry' : 'testOpenAction')}
            onOpen={() => void runTestOpen()}
            inactive={!canOpen}
            busy={opening}
          />
          <CheckRow
            state={checks.open}
            label={t('checkOpenLabel')}
            detail={t(
              checks.open === 'running'
                ? 'checkOpenRunning'
                : checks.open === 'passed'
                  ? 'checkOpenPassed'
                  : checks.open === 'failed'
                    ? 'checkOpenFailed'
                    : checks.open === 'skipped'
                      ? 'checkSkipped'
                      : 'checkPending',
            )}
          />
        </ol>
        {/*
          The one repair a failed probe most often needs, and the one the installer is standing in
          front of. A SEAV controller held by the vendor app answers nothing over the cloud, which
          from here is indistinguishable from no power and no reception — so the notice names the
          check to make rather than a verdict, and stays informational (blue, `info`) rather than
          borrowing the red the failed row already carries.
        */}
        {checks.status === 'failed' ? (
          <div className="install-notice" role="note">
            <span className="install-notice-glyph" aria-hidden="true">
              <Icon name="info" size={18} />
            </span>
            <span className="install-notice-text">{t('checkStatusSeavHint')}</span>
          </div>
        ) : null}
        {confirmed ? <p className="hint centered">{t('testOpenSuccess')}</p> : null}
        {/* D140: the command was accepted and nothing looked again, so the only instrument
                left is the installer standing in front of the gate. Say that, and do not dress
                it as a passed test. */}
        {unverified ? <p className="hint centered">{t('testOpenUnverified')}</p> : null}
        {/* An unverified open is the case most worth retrying — an unpowered gate, a
                controller still joining, a leaf that had not started moving. The check-2 tile
                itself becomes that retry, so there is no second control for it. */}
        {checks.move === 'failed' ? <p className="hint error">{t('testOpenFailed')}</p> : null}
        {mayContinue
          ? footer(t('continue'), () => setStep('owner'))
          : footer(t('skipTest'), skipTest, false, true)}
      </>
    )
  }

  if (step === 'owner') {
    const ownerSubmitDisabled = ownerPhone.trim().length === 0 || ownerBusy
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!ownerSubmitDisabled) void sendOwnerInvite()
        }}
      >
        <AppBar title={t('ownerTitle')} onBack={() => setStep('testOpen')} />
        {/*
          The same one-off picker the `invite` flow offers, and for a stronger reason: the installer
          is standing at the gate typing a number they were told out loud, and this step is the one
          place in the flow where getting it wrong hands the gate to a stranger. It renders nothing
          on a browser without the capability, so the manual field below stays the whole flow there.
        */}
        <ContactPickerBlock
          value={ownerPhone}
          onPick={(phone) => {
            setOwnerPhone(phone)
            setOwnerError(undefined)
          }}
        />
        <div className="form-row">
          <label htmlFor="ownerPhone">{t('ownerPhoneLabel')}</label>
          <input
            id="ownerPhone"
            className="input"
            type="tel"
            dir="ltr"
            value={ownerPhone}
            onChange={(e) => {
              setOwnerPhone(e.target.value)
              // R1-7(e)'s refusal names the number that was refused, so it cannot outlive it.
              setOwnerError(undefined)
            }}
          />
        </div>
        <div className="form-row">
          <label id="ownerRoleLabel">{t('ownerRoleLabel')}</label>
          <div className="filter-row" role="group" aria-labelledby="ownerRoleLabel">
            <button
              className={`filter-chip${ownerRole === 'manager' ? ' on' : ''}`}
              type="button"
              aria-pressed={ownerRole === 'manager'}
              onClick={() => setOwnerRole('manager')}
            >
              {t('roleManager')}
            </button>
            <button
              className={`filter-chip${ownerRole === 'resident' ? ' on' : ''}`}
              type="button"
              aria-pressed={ownerRole === 'resident'}
              onClick={() => setOwnerRole('resident')}
            >
              {t('roleResident')}
            </button>
          </div>
        </div>
        {ownerRole === 'resident' ? <p className="hint">{t('ownerRoleResidentHint')}</p> : null}
        {ownerError !== undefined ? (
          <p className="hint error">
            {t(ownerError === 'invalid' ? 'ownerPhoneInvalid' : 'ownerInviteFailed')}
          </p>
        ) : null}
        {footer(t('continue'), () => void sendOwnerInvite(), ownerSubmitDisabled, false, undefined, true)}
        {footer(t('skip'), () => setStep('done'), false, true)}
      </form>
    )
  }

  if (step === 'share') {
    return (
      <>
        <AppBar title={t('shareStepTitle')} onBack={() => setStep('owner')} />
        {invite !== undefined ? (
          <ShareSheet
            heading={t('shareStepTitle')}
            message={invite.share.text}
            onSelect={(channel) => {
              if (channel === 'sms')
                window.open(`sms:?body=${encodeURIComponent(invite.share.text)}`, '_blank')
              else if (channel === 'whatsapp')
                window.open(`https://wa.me/?text=${encodeURIComponent(invite.share.text)}`, '_blank')
              else if (navigator.share !== undefined)
                void navigator.share({ text: invite.share.text, url: invite.share.url })
            }}
          />
        ) : null}
        {footer(t('skip'), () => setStep('done'), false, true)}
      </>
    )
  }

  // step === 'done'
  return (
    <>
      <AppBar title={t('title')} onBack={() => navigate.back()} />
      <div style={{ padding: '30px 18px', textAlign: 'center' }}>
        <div style={{ fontWeight: 700, fontSize: 'var(--font-cardTitle)', marginBottom: 8 }}>
          {t('allSetTitle')}
        </div>
        <div style={{ fontSize: 'var(--font-control)', color: 'var(--muted)', marginBottom: 10 }}>
          {t(ownerRole === 'resident' ? 'allSetBodyResident' : 'allSetBody', { name: defaultName })}
        </div>
      </div>
      {footer(t('finish'), finish)}
    </>
  )
}
